import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plane, Search, ArrowRight, AlertTriangle, CheckCircle, Check, Clock, Luggage,
  RefreshCw, ChevronLeft, Info, Trash2,
} from 'lucide-react';
import {
  FLIGHT_ENDPOINTS, CABIN_CLASSES, offerPrice, offerCurrency, priceIsConverted, offerExpired,
  minutesUntilExpiry, segmentsFor, flightNumberOf, airlineOf,
  formatDuration, timeOf, includedBaggage, fareTerms,
  searchAirports, looksLikeIata, airportLabel, hasReturnLeg, returnSectorFrom,
  flightDocumentsFor, normalizeDocumentType,
  baggageBySegment, baggageOptions, bagLabel, servicePrice, servicesPayload, servicesTotal,
  passengerRefFor, passengerRoleLabel,
  airlineLoyaltyFromProfile, resolveAirlineCode, validLoyaltyPrograms, loyaltyNumberWarning,
  seatOptions, seatsBySegment, seatRows, seatAvailable, seatNumber, seatDescription,
  seatAssistanceConcern,
  SORT_MODES, groupByLeg, sortLegGroups, sortJourneys,
} from '../../utils/flightBooking';
import { ssrRequirements, needsAdvanceNotice } from '../../data/ssrCodes';
import { resolveTravellerProfile } from '../../utils/passengerNames';
import { paymentReturnUrl } from '../../utils/hotelBooking';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';

const NUITEE_KEY        = process.env.REACT_APP_NUITEE_API_KEY || '';
const NUITEE_IS_SANDBOX = NUITEE_KEY.startsWith('sand_');

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500';
const lbl = 'block text-xs font-medium text-gray-700 mb-1';


const money = (v, c = 'AUD') =>
  `${c === 'AUD' ? 'A$' : c + ' '}${(parseFloat(v) || 0).toFixed(2)}`;

// Steps differ by mode: a quote stops once an option is chosen, since there is nothing to pay for.
const FLIGHT_STATE_KEY = 'flight_booking_state';

const BOOK_STEPS  = ['Search', 'Flights', 'Fare', 'Passengers', 'Confirm', 'Extras', 'Payment'];
const QUOTE_STEPS = ['Search', 'Flights', 'Fare'];

/**
 * `retries` defaults to 0 and MUST stay that way for anything that creates state.
 *
 * The rates endpoint fails intermittently — measured at 3 of 6 identical calls returning
 * 500 "failed to search flights" — so search and verify retry, being read-only and idempotent.
 * Prebook holds a fare and booking takes money: retrying either could leave duplicate holds or,
 * far worse, double-book and double-charge a traveller. A failed booking is for a human to look at,
 * not for the client to quietly repeat.
 */
async function liteApi(url, body, { retries = 0 } = {}) {
  let res, text;
  for (let attempt = 0; ; attempt++) {
    res  = await fetch(url, {
      method: 'POST',
      headers: { 'X-API-Key': NUITEE_KEY, 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    });
    text = await res.text();
    // Only a server-side wobble is worth repeating; a 4xx means the request itself is wrong.
    const transient = res.status >= 500 || res.status === 429;
    if (res.ok || !transient || attempt >= retries) break;
    await new Promise(r => setTimeout(r, 700 * (attempt + 1)));
  }
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* non-JSON error body */ }
  if (!res.ok) {
    // `description` carries the actionable detail ("Departure date is too far in the future
    // (411 days). Maximum is 365 days"); `message` is a generic category ("unable to process
    // search request"). Reading message first hid the only useful half of every error.
    const e = json?.error || {};
    const msg = e.description || e.message || json?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.apiCode = e.code;
    throw err;
  }
  return json;
}

// Airlines do not sell beyond ~12 months; the API rejects anything over 365 days out. Bounding the
// picker means nobody discovers that by waiting for a failed search.
const MAX_ADVANCE_DAYS = 365;

function dateBounds() {
  const today = new Date();
  const max   = new Date(today.getTime() + MAX_ADVANCE_DAYS * 86400000);
  const iso   = d => d.toISOString().slice(0, 10);
  return { min: iso(today), max: iso(max) };
}

// ── LiteAPI payment widget (same wrapper the hotel flow uses) ─────────────────

function PaymentWidget({ secretKey, returnUrl, onError }) {
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (!secretKey || !returnUrl) return;

    const initPayment = () => {
      if (!window.LiteAPIPayment) {
        onError('Payment SDK failed to load. Please refresh and try again.');
        return;
      }
      try {
        new window.LiteAPIPayment({
          publicKey:     NUITEE_IS_SANDBOX ? 'sandbox' : 'live',
          appearance:    { theme: 'flat' },
          options:       { business: { name: 'STX Travel' } },
          targetElement: '#liteapi-flight-payment-form',
          secretKey,
          returnUrl,
        }).handlePayment();
      } catch (e) {
        onError('Payment form failed to initialise: ' + e.message);
      }
    };

    if (scriptLoadedRef.current) { initPayment(); return; }
    const existing = document.querySelector('script[data-liteapi-payment]');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.src = 'https://payment-wrapper.liteapi.travel/dist/liteAPIPayment.js?v=a1';
    script.setAttribute('data-liteapi-payment', 'true');
    script.onload  = () => { scriptLoadedRef.current = true; initPayment(); };
    script.onerror = () => onError('Could not load the payment SDK. Check your connection and try again.');
    document.head.appendChild(script);
  }, [secretKey, returnUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div id="liteapi-flight-payment-form" className="w-full min-h-[300px]" />;
}

/**
 * Airport picker. The rates endpoint accepts IATA codes only, so this resolves what someone
 * actually types — "Brisbane" — into BNE rather than failing with a missing-field error.
 *
 * Accepts a bare 3-letter code directly for anyone who knows it, and resolves a city name passed
 * in from the trip on mount, so the sector's "Brisbane" prefill does not have to be retyped.
 */
function AirportInput({ id, label, value, onChange, placeholder, prefillCity }) {
  const [text, setText]       = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [busy, setBusy]       = useState(false);
  const [resolvedFrom, setResolvedFrom] = useState('');
  const [lookupFailed, setLookupFailed] = useState(false);
  const boxRef = useRef(null);

  // Resolve a city name handed in from the trip once, on mount.
  useEffect(() => {
    let cancelled = false;
    const seed = (prefillCity || '').trim();
    // Arriving with an already-resolved code (re-opening a saved option): show it rather than
    // leaving the box looking empty next to a "Searching BNE" hint.
    if (value && !text) { setText(value); return; }
    if (!seed || value) return;
    if (looksLikeIata(seed)) { onChange(seed.toUpperCase()); setText(seed.toUpperCase()); return; }
    (async () => {
      const rows = await searchAirports(seed, NUITEE_KEY);
      if (cancelled || !rows || rows.length === 0) return;
      onChange(rows[0].iata);
      setText(airportLabel(rows[0]));
      setResolvedFrom(seed);
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced lookup as they type.
  useEffect(() => {
    const q = text.trim();
    if (!q || q.length < 2 || q === airportLabel(results.find(r => r.iata === value))) return;
    if (looksLikeIata(q)) { onChange(q.toUpperCase()); return; }
    setBusy(true);
    const id2 = setTimeout(async () => {
      const rows = await searchAirports(q, NUITEE_KEY);
      setLookupFailed(rows === null);
      setResults(rows || []);
      setOpen((rows || []).length > 0);
      setBusy(false);
    }, 300);
    return () => { clearTimeout(id2); setBusy(false); };
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (a) => { onChange(a.iata); setText(airportLabel(a)); setOpen(false); setResolvedFrom(''); };

  return (
    <div ref={boxRef} className="relative">
      <label className={lbl} htmlFor={id}>{label}</label>
      <input
        id={id}
        className={inp}
        value={text}
        onChange={e => { setText(e.target.value); if (!e.target.value.trim()) onChange(''); }}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        aria-describedby={`${id}-hint`}
      />
      <p id={`${id}-hint`} className="text-xs text-gray-600 mt-1">
        {value
          ? <>Searching <strong>{value}</strong>{resolvedFrom && ` — matched from "${resolvedFrom}"`}</>
          : busy ? 'Looking up airports…'
          : lookupFailed ? <span className="text-amber-800">Airport lookup is busy — try again in a moment, or enter the 3-letter code.</span>
          : text.trim().length >= 2 && results.length === 0 ? 'No airports match that — try the city name.'
          : 'Type a city or airport name, or a 3-letter code'}
      </p>

      {open && results.length > 0 && (
        <ul id={`${id}-listbox`} role="listbox" aria-label={`${label} results`}
          className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg">
          {results.map(a => (
            <li key={a.iata} role="option" aria-selected={a.iata === value}>
              <button type="button" onClick={() => pick(a)}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none">
                <span className="text-sm text-gray-900">
                  {a.city || a.name} <span className="font-mono text-gray-700">({a.iata})</span>
                </span>
                <span className="block text-xs text-gray-600">
                  {a.name}{a.country ? ` · ${a.country}` : ''}
                  {!a.hasAirlineService && ' · limited service'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StepBar({ steps, current }) {
  return (
    <ol className="flex items-center gap-1.5 flex-wrap mb-5" aria-label="Booking progress">
      {steps.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'now' : 'todo';
        return (
          <li key={s} className="flex items-center gap-1.5">
            <span
              aria-current={state === 'now' ? 'step' : undefined}
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                state === 'done' ? 'bg-blue-100 text-blue-800'
                : state === 'now' ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600'}`}
            >
              {s}
            </span>
            {i < steps.length - 1 && <span className="text-gray-400" aria-hidden="true">›</span>}
          </li>
        );
      })}
    </ol>
  );
}

// ── Journey / offer rendering ─────────────────────────────────────────────────

function SegmentRow({ segment }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      {segment.carrier?.marketingLogo && (
        <img src={segment.carrier.marketingLogo} alt="" className="w-6 h-6 object-contain" />
      )}
      <span className="font-mono text-gray-800 w-16">{flightNumberOf(segment)}</span>
      <span className="text-gray-800">
        {timeOf(segment.departureTime)} {segment.originCode}
      </span>
      <ArrowRight size={13} aria-hidden="true" className="text-gray-500" />
      <span className="text-gray-800">
        {timeOf(segment.arrivalTime)} {segment.destinationCode}
      </span>
      <span className="text-gray-600 text-xs">{formatDuration(segment.duration)}</span>
    </div>
  );
}

/**
 * A blank ticket-name form seeded from whatever the portal knows about one passenger.
 *
 * Seeded but fully editable: the airline needs the name exactly as it appears on the travel
 * document, and the portal's record is not authoritative for that. `profile` is kept on the form so
 * documents and frequent flyer numbers can be offered per passenger rather than per booking.
 */
function newPaxForm(profile, fallbackName = '') {
  const parts = String(fallbackName || '').trim().split(/\s+/).filter(Boolean);
  const g = String(profile?.gender || '').toUpperCase();
  return {
    name: fallbackName || '',
    role: profile?.role || 'traveller',
    profile: profile || null,
    firstName:  profile?.firstName  || parts[0] || '',
    middleName: profile?.middleName || '',
    lastName:   profile?.lastName   || (parts.length > 1 ? parts.slice(1).join(' ') : ''),
    birthday:   profile?.dateOfBirth || '',
    // The API takes M, F or X and rejects anything else. 'Prefer not to say' correctly sends
    // nothing; 'Non-binary' maps to X rather than being silently dropped.
    gender: g.startsWith('F') ? 'F' : g.startsWith('M') ? 'M' : g.startsWith('N') ? 'X' : '',
    nationality:          'AU',
    documentType:         'id',
    documentNumber:       '',
    documentIssueCountry: 'AU',
    documentExpiry:       '',
    docChoice:     '',
    loyalty:       [],
    loyaltySeeded: false,
    nameConfirmed: false,
  };
}

function FlightRow({ segments, price, currency, priceLabel, priceNote, note, selected, onSelect }) {
  const first = segments[0];
  const last  = segments[segments.length - 1];
  if (!first) return null;
  const stops = Math.max(0, segments.length - 1);

  return (
    <button
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full text-left border rounded-lg p-4 transition ${
        selected ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/30'}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 text-gray-900">
            <span className="text-lg font-semibold tabular-nums">{timeOf(first.departureTime)}</span>
            <span className="text-sm text-gray-600">{first.originCode}</span>
            <ArrowRight size={14} aria-hidden="true" className="text-gray-500" />
            <span className="text-lg font-semibold tabular-nums">{timeOf(last.arrivalTime)}</span>
            <span className="text-sm text-gray-600">{last.destinationCode}</span>
          </div>
          <p className="text-xs text-gray-700 mt-1 flex items-center gap-1.5 flex-wrap">
            {first.carrier?.marketingLogo && (
              <img src={first.carrier.marketingLogo} alt="" className="w-4 h-4 object-contain" />
            )}
            <span>{airlineOf(first)}</span>
            <span className="font-mono text-gray-600">{segments.map(x => flightNumberOf(x)).join(' · ')}</span>
            <span aria-hidden="true">·</span>
            <span>{stops === 0 ? 'Direct' : `${stops} stop${stops > 1 ? 's' : ''}`}</span>
            {note && (<><span aria-hidden="true">·</span><span className="text-gray-600">{note}</span></>)}
          </p>
        </div>
        <div className="text-right whitespace-nowrap">
          {/* The label is an eyebrow, not fine print. On a return search this figure is the
              whole-trip fare, and a quiet grey "return from" beside a large number reads as the
              price of the single flight being looked at — which is what it was mistaken for. */}
          {priceLabel && (
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-600">{priceLabel}</p>
          )}
          <p className="text-lg font-semibold text-gray-900 tabular-nums">{money(price, currency)}</p>
          {priceNote && <p className="text-xs font-medium text-blue-700 mt-0.5">{priceNote}</p>}
        </div>
      </div>
    </button>
  );
}

/**
 * Shown when the fare is priced in a foreign currency and the A$ figure is a conversion.
 *
 * Flight fares on this account are not natively AUD — see `priceIsConverted`. The amount is still
 * taken in AUD (measured on the payment intent), so this does not warn about being charged in
 * another currency; it warns that the figure is an exchange-rate conversion made at search time,
 * which is why a card statement can read a few dollars out and can carry a cross-border fee. The
 * alternative — saying nothing — is what left a live booking looking like the portal had quoted
 * one amount and charged another.
 */
function FxNotice({ offer, currency }) {
  if (!priceIsConverted(offer)) return null;
  return (
    <p className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
      <Info size={13} aria-hidden="true" className="mt-0.5 shrink-0" />
      <span>
        The airline prices this fare in a foreign currency. The {currency} amount is Nuitee's
        conversion at the time of search, so the figure that finally settles can differ slightly and
        your card may add a cross-border or foreign-currency fee.
      </span>
    </p>
  );
}

// ── Main flow ─────────────────────────────────────────────────────────────────

export default function FlightBookingFlow({
  tripId, sectorIndex, clientId, mode = 'book', savedOption,
  originPre, destinationPre, datePre, airlinePre, ssrCodes = [], ssrOther = '',
  travellerName, travellerId = '', email, passengerProfile, passengerList = [],
  onBooked, onSaveOption, needsTripSave = false,
}) {
  const steps = mode === 'quote' ? QUOTE_STEPS : BOOK_STEPS;

  const [step, setStep]       = useState('search');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');

  // ── Search criteria ────────────────────────────────────────────────────────
  // These hold a resolved IATA code, never free text — AirportInput owns the text and resolves it.
  // A city name reaching /flights/rates returns "required search field is missing".
  const [origin, setOrigin]           = useState(savedOption?.origin      || '');
  const [destination, setDestination] = useState(savedOption?.destination || '');
  const [date, setDate]               = useState(savedOption?.date        || datePre        || '');
  const [returnDate, setReturnDate]   = useState(savedOption?.returnDate  || '');
  const [cabinClass, setCabinClass]   = useState(savedOption?.cabinClass  || 'ECONOMY');
  // ── Results ────────────────────────────────────────────────────────────────
  const [journeys, setJourneys]   = useState([]);
  const [currency, setCurrency]   = useState('AUD');
  const [journey, setJourney]     = useState(null);
  // Empty means no filter. The API has no airline filter of its own (checked against the rates
  // schema), so this works on the results — which is better anyway: every carrier stays visible
  // with its count and cheapest fare, rather than being excluded before anyone sees it.
  const [airlineFilter, setAirlineFilter] = useState([]);
  // Departure order by default. Corporate travel is decided by the meeting time far more often
  // than by price, so the schedule is the useful view and cheapest is the alternative.
  const [sortMode, setSortMode] = useState('time');
  // On a return, which outbound has been chosen. Null means we are still picking it.
  const [outboundKey, setOutboundKey] = useState(null);
  const [offer, setOffer]         = useState(null);

  // ── Passenger + contact (book mode only) ───────────────────────────────────
  // Seeded from the portal but fully editable: the airline needs the name exactly as it appears
  // on the travel document, and the portal's record is not authoritative for that.
  const seedName = (savedOption?.travellerName || travellerName || '').trim();

  // One form per person on the booking. Supported travel routinely puts a support worker on the
  // same aircraft, and booking them separately risks a different fare and seats apart — so they
  // ride on one prebook, in the order the trip lists them, which is the order `PAX1`/`PAX2` mean.
  const [paxList, setPaxList] = useState(() =>
    (passengerList.length > 0 ? passengerList : [{ name: seedName, role: 'traveller' }])
      .map(entry => newPaxForm(entry, entry.name || seedName)));

  const [activePax, setActivePax] = useState(0);
  const pax = paxList[activePax] || paxList[0];
  const setPax = (updater) => setPaxList(list => list.map((p, i) =>
    i === activePax ? (typeof updater === 'function' ? updater(p) : { ...p, ...updater }) : p));

  // The number of seats searched for IS the number of people ticketed — they cannot drift apart,
  // since a fare priced for one seat cannot carry two.
  const adults = paxList.length;

  // The client's passenger profiles, so someone already in the portal can be put on the booking
  // by name rather than having their passport retyped. The popup runs inside the authenticated
  // app, so this is the same read the trip form does.
  const [clientPassengers, setClientPassengers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerText, setPickerText] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualName, setManualName] = useState({ firstName: '', lastName: '' });
  const [manualRole, setManualRole] = useState('support_worker');

  // Loaded here as well as in the opener, and that redundancy is load-bearing — see the
  // profile-recovery effect below.
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  useEffect(() => {
    if (!clientId) { setProfilesLoaded(true); return; }
    getDocs(collection(db, 'clients', clientId, 'passengers'))
      .then(snap => setClientPassengers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      // Swallowed before, which meant a failed read looked exactly like a traveller who has no
      // profile: blank fields, no explanation. The flag drives the notice on the passenger step.
      .catch(() => setProfileLoadFailed(true))
      .finally(() => setProfilesLoaded(true));
  }, [clientId]);

  /**
   * Who the trip says is travelling, read from the trip rather than taken from the opener.
   *
   * ⚠️ Needed because the popup does NOT always know the traveller's name. The hand-off keeps only
   * the routing keys on the URL — tripId, sectorIndex, clientId, mode, openedFrom — and stashes
   * the rest under a handle that is consumed on first read. So after a reload, and after the
   * payment wrapper navigates this window and comes back, `travellerName` and `travellerId` are
   * both gone while tripId and clientId are still right there. Reading the trip is what lets the
   * profile recovery below work in those cases instead of giving up.
   */
  const [tripTraveller, setTripTraveller] = useState({ name: '', id: '' });
  useEffect(() => {
    if (!clientId || !tripId) return;
    getDoc(doc(db, 'clients', clientId, 'trips', tripId))
      .then(snap => {
        if (!snap.exists()) return;
        const t = snap.data() || {};
        setTripTraveller({ name: t.travellerName || '', id: t.travellerId || '' });
      })
      .catch(() => { /* the opener's values, where it supplied any, still stand */ });
  }, [clientId, tripId]);

  const profileName = (p) =>
    [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || p.preferredName || '';

  // Profiles not already on this booking, matched on the text typed into the picker.
  const pickerMatches = useMemo(() => {
    const onBooking = new Set(paxList.map(p => p.profile?.id).filter(Boolean));
    const q = pickerText.trim().toLowerCase();
    return clientPassengers
      .filter(p => !onBooking.has(p.id))
      .filter(p => !q || profileName(p).toLowerCase().includes(q))
      .sort((a, b) => profileName(a).localeCompare(profileName(b)))
      .slice(0, 8);
  }, [clientPassengers, paxList, pickerText]);

  const addFromProfile = (profile) => {
    setPaxList(list => [...list, newPaxForm(
      { ...profile, role: 'support_worker' }, profileName(profile))]);
    setPickerOpen(false);
    setPickerText('');
  };

  // Attach a profile to someone ALREADY on the booking, rather than adding a passenger.
  //
  // The manual way out when automatic resolution finds nothing — a profile named differently from
  // the trip, or a traveller whose profile was created after the trip. Without this the only
  // option is retyping a passport that the portal already holds, which is both slower and more
  // likely to be wrong than the record it is duplicating.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const linkMatches = useMemo(() => {
    // Profiles attached to OTHER rows are excluded: the same person cannot be two passengers.
    const taken = new Set(paxList.filter((_, i) => i !== activePax)
      .map(p => p.profile?.id).filter(Boolean));
    const q = linkText.trim().toLowerCase();
    return clientPassengers
      .filter(p => !taken.has(p.id))
      .filter(p => !q || profileName(p).toLowerCase().includes(q))
      .sort((a, b) => profileName(a).localeCompare(profileName(b)))
      .slice(0, 8);
  }, [clientPassengers, paxList, activePax, linkText]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkProfile = (profile) => {
    const g = String(profile.gender || '').toUpperCase();
    setPax(p => ({
      ...p,
      profile,
      firstName:  profile.firstName  || p.firstName,
      middleName: profile.middleName || p.middleName,
      lastName:   profile.lastName   || p.lastName,
      birthday:   profile.dateOfBirth || p.birthday,
      gender: g.startsWith('F') ? 'F' : g.startsWith('M') ? 'M' : g.startsWith('N') ? 'X' : p.gender,
      // Cleared so the document picker re-seeds from this profile's documents rather than keeping
      // whatever was chosen for the wrong person.
      docChoice: '',
      loyalty: [],
      loyaltySeeded: false,
      // The ticket name has changed, so the confirmation it matches the document no longer holds.
      nameConfirmed: false,
    }));
    if (activePax === 0) {
      setContact(c => ({
        ...c,
        email:       c.email || profile.email || '',
        phoneNumber: c.phoneNumber
          || String(profile.phone || '').replace(/^\+?61/, '').replace(/\D/g, ''),
      }));
    }
    setLinkOpen(false);
    setLinkText('');
  };

  const addManualPassenger = () => {
    const firstName = manualName.firstName.trim();
    const lastName  = manualName.lastName.trim();
    if (!firstName || !lastName) return;
    // Built from the entered parts rather than a joined-then-split name, so a multi-word surname
    // survives intact all the way to the ticket.
    setPaxList(list => [...list, {
      ...newPaxForm({ role: manualRole }, `${firstName} ${lastName}`),
      firstName, lastName,
      name: `${firstName} ${lastName}`,
    }]);
    setPickerOpen(false);
    setPickerText('');
    setManualOpen(false);
    setManualName({ firstName: '', lastName: '' });
    setManualRole('support_worker');
  };
  const removePassenger = (i) => {
    if (paxList.length <= 1) return;
    setPaxList(list => list.filter((_, j) => j !== i));
    setActivePax(a => Math.min(a, paxList.length - 2));
    // Bags and seats are keyed to a positional ref (PAX1, PAX2…), so dropping someone would
    // silently re-point every later selection at the wrong person. Clear and re-choose.
    setBagChoice([]);
    setSeatChoice({});
  };

  // Documents held on each passenger profile, best first (valid, then passports — a licence is
  // domestic-only). Offered as a picker so nothing has to be retyped, but every field stays
  // editable: the profile can be out of date and the airline only cares about the document present
  // on the day.
  const docsByPax = useMemo(
    () => paxList.map(p => flightDocumentsFor(p.profile?.identityDocuments || [], date)),
    [paxList, date],
  );
  const profileDocs = docsByPax[activePax] || [];
  const docChoice = pax?.docChoice || '';
  const setDocChoice = (v) => setPax(p => ({ ...p, docChoice: v }));

  const loyalty = pax?.loyalty || [];
  const [loyaltyResolving, setLoyaltyResolving] = useState(false);

  // Frequent flyer programmes, seeded from each profile. Editable, because the profile stores a
  // free-text programme name and not every one resolves — "Velocity" and "KrisFlyer" are programme
  // names rather than airline names and never will. Resolved for everyone at once: the lookup is
  // rate-limited, and doing it per passenger as they are clicked through would stall the form.
  useEffect(() => {
    if (step !== 'passengers') return;
    if (paxList.every(p => p.loyaltySeeded)) return;
    let cancelled = false;
    setLoyaltyResolving(true);
    (async () => {
      const resolvedPerPax = [];
      for (const p of paxList) {
        if (p.loyaltySeeded) { resolvedPerPax.push(null); continue; }
        const fromProfile = airlineLoyaltyFromProfile(p.profile?.loyaltyPrograms);
        const rows = [];
        for (const lp of fromProfile) {
          const code = await resolveAirlineCode(lp.program, NUITEE_KEY);
          rows.push({ airlineCode: code || '', programNumber: lp.number, sourceName: lp.program });
        }
        resolvedPerPax.push(rows);
      }
      if (cancelled) return;
      setPaxList(list => list.map((p, i) => resolvedPerPax[i] === null
        ? p
        : { ...p, loyalty: p.loyalty.length > 0 ? p.loyalty : resolvedPerPax[i], loyaltySeeded: true }));
      setLoyaltyResolving(false);
    })();
    return () => { cancelled = true; };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLoyalty = (i, key, value) =>
    setPax(p => ({ ...p, loyalty: p.loyalty.map((l, j) => j === i ? { ...l, [key]: value } : l) }));
  const addLoyalty    = () =>
    setPax(p => ({ ...p, loyalty: [...p.loyalty, { airlineCode: '', programNumber: '', sourceName: '' }] }));
  const removeLoyalty = (i) =>
    setPax(p => ({ ...p, loyalty: p.loyalty.filter((_, j) => j !== i) }));

  const applyDoc = (doc) => setPax(p => ({
    ...p,
    documentType:         doc.type,
    documentNumber:       doc.number,
    documentExpiry:       doc.expiry,
    // Only overwrite the country when the profile actually resolved one — issuingCountryCode
    // returns blank rather than guessing, and a wrong issuing country on a ticket matters.
    documentIssueCountry: doc.country || p.documentIssueCountry,
  }));

  // Pre-fill every passenger from their own best document, once the step is first reached.
  useEffect(() => {
    if (step !== 'passengers') return;
    setPaxList(list => list.map((p, i) => {
      const docs = docsByPax[i] || [];
      if (p.docChoice || docs.length === 0) return p;
      const d = docs[0];
      return {
        ...p,
        docChoice: d.number,
        documentType: d.type, documentNumber: d.number, documentExpiry: d.expiry,
        documentIssueCountry: d.country || p.documentIssueCountry,
      };
    }));
  }, [step, docsByPax]); // eslint-disable-line react-hooks/exhaustive-deps
  // One contact for the booking, not one per passenger: the airline sends the itinerary and any
  // disruption notice to a single address, and that is the traveller's.
  const [contact, setContact] = useState(() => {
    const lead = (passengerList[0] || passengerProfile || {});
    return {
      email:            email || lead.email || '',
      phoneCountryCode: '61',
      phoneNumber:      String(lead.phone || '').replace(/^\+?61/, '').replace(/\D/g, ''),
    };
  });
  const nameConfirmed = pax?.nameConfirmed || false;
  const setNameConfirmed = (v) => setPax(p => ({ ...p, nameConfirmed: v }));
  // Assistance cannot ride on the booking (see src/data/ssrCodes.js), so the person booking has
  // to confirm they will arrange it with the airline before the fare is held.
  const [ssrConfirmed, setSsrConfirmed] = useState(false);

  // ── Prebook / payment ──────────────────────────────────────────────────────
  const [verifyChanges, setVerifyChanges] = useState(null);
  const [prebookId, setPrebookId]   = useState('');
  const [secretKey, setSecretKey]   = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [finalPrice, setFinalPrice] = useState(null);
  const [storageWarning, setStorageWarning] = useState(false);
  // The whole prebook response — servicesAttachable lives on it, and each services call returns
  // a fresh one that supersedes it.
  const [prebookRow, setPrebookRow] = useState(null);
  // What to show once everything is over: the booking reference on success, or what went wrong.
  // Terminal either way — the window stays open until the booker closes it.
  const [bookingOutcome, setBookingOutcome] = useState(null);
  const [bagChoice, setBagChoice]   = useState([]);   // the services themselves, not just ids
  // One seat per segment. Keyed by segmentKey so choosing again on the same leg replaces
  // rather than adds — you cannot sit in two seats on one flight.
  const [seatChoice, setSeatChoice] = useState({});
  // Set on the post-payment reload, when there is no offer in state to price the header from.
  const [confirming, setConfirming] = useState(false);
  const [paidPrice, setPaidPrice]   = useState(null);

  /**
   * Recover the traveller's passenger profile here when the opener did not supply one.
   *
   * ⚠️ The opener resolving the profile is NOT enough on its own, and when it misses, every
   * pre-filled field — date of birth, passport, contact details — arrives blank with no
   * explanation, which looks exactly like a traveller who has no profile at all. The booker then
   * retypes a passport by hand, or puts their own email on the booking as the airline's contact.
   * The opener misses whenever its own passenger list had not arrived at the moment the button was
   * clicked, whenever this popup is reloaded (the stashed context is consumed on first read and
   * only the few URL keys survive), and whenever an already-open booking window is reused rather
   * than reopened — none of which the opener can detect.
   *
   * This popup loads every profile for the client regardless, for the add-passenger picker, so the
   * same resolution runs again here against a list that is definitely loaded. Only fields the
   * booker has not already filled are seeded: this can land a second after the window opens, and
   * overwriting something typed by hand would be worse than the blank it fixes.
   */
  useEffect(() => {
    if (clientPassengers.length === 0) return;
    const name = seedName || travellerName || tripTraveller.name || '';
    const uid  = travellerId || tripTraveller.id || '';
    if (!name && !uid) return;
    const found = resolveTravellerProfile(
      { travellerName: name, travellerId: uid }, clientPassengers);
    if (!found) return;
    setPaxList(list => list.map((p, i) => {
      if (i !== 0 || p.profile) return p;      // the traveller only, and only when none was passed
      const g = String(found.gender || '').toUpperCase();
      return {
        ...p,
        profile:    found,
        // Kept in step with the names, since this is what the trip's passenger record is written
        // from — on a reload it arrives blank along with everything else.
        name:       p.name || profileName(found),
        firstName:  p.firstName  || found.firstName   || '',
        middleName: p.middleName || found.middleName  || '',
        lastName:   p.lastName   || found.lastName    || '',
        birthday:   p.birthday   || found.dateOfBirth || '',
        gender: p.gender
          || (g.startsWith('F') ? 'F' : g.startsWith('M') ? 'M' : g.startsWith('N') ? 'X' : ''),
      };
    }));
    // The airline sends the itinerary and any disruption notice to this address, so it has to be
    // the traveller's and not whoever happened to be at the keyboard.
    setContact(c => ({
      ...c,
      email:       c.email || found.email || '',
      phoneNumber: c.phoneNumber
        || String(found.phone || '').replace(/^\+?61/, '').replace(/\D/g, ''),
    }));
  }, [clientPassengers, travellerId, travellerName, seedName, tripTraveller]);

  const stepIndex = Math.max(0, steps.indexOf(
    { search: 'Search', results: 'Flights', detail: 'Fare', passengers: 'Passengers',
      confirm: 'Confirm', bags: 'Extras', payment: 'Payment' }[step] || 'Search'));

  const ssrNeeded = useMemo(() => ssrRequirements(ssrCodes, ssrOther), [ssrCodes, ssrOther]);

  // The name each passenger will be ticketed under, against the portal's record of them.
  const paxNameOf = (p) => [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ').trim();
  const portalNameOf = (p) =>
    [p.profile?.firstName, p.profile?.lastName].filter(Boolean).join(' ').trim() || p.name || '';
  const sameName = (a, b) =>
    a.toLowerCase().replace(/\s+/g, ' ') === b.toLowerCase().replace(/\s+/g, ' ');

  // The active passenger, for the form. Everyone's names are shown together on the quote and
  // confirm steps, where the whole booking is being reviewed rather than one person edited.
  const portalName  = portalNameOf(pax || {});
  const enteredName = paxNameOf(pax || {});
  const nameDiffers = portalName && enteredName && !sameName(portalName, enteredName);

  /** Every ticket name, for the record written to the trip and for the review panels. */
  const allTicketNames = paxList.map(paxNameOf).filter(Boolean);

  // Offers go stale ~15 minutes after search; warn before someone commits to a dead price.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!offer) return;
    const id = setInterval(() => forceTick(n => n + 1), 30000);
    return () => clearInterval(id);
  }, [offer]);
  const expiryMins = offer ? minutesUntilExpiry(offer) : null;
  const isExpired  = offer ? offerExpired(offer) : false;

  // Carriers present in these results, with how many options each has and its cheapest fare.
  // Keyed on the marketing carrier of the first outbound segment — the airline whose name is on
  // the ticket, which is what someone means when they say "Qantas only".
  const airlines = useMemo(() => {
    const map = new Map();
    for (const j of journeys) {
      const seg = segmentsFor(j, 'OUTBOUND')[0];
      const code = seg?.carrier?.marketingCode;
      if (!code) continue;
      const price = offerPrice(j.cheapestOffer || (j.offers || [])[0]);
      const prev = map.get(code);
      if (!prev) {
        map.set(code, { code, name: airlineOf(seg), logo: seg.carrier?.marketingLogo, count: 1, from: price });
      } else {
        prev.count += 1;
        if (price > 0 && (prev.from === 0 || price < prev.from)) prev.from = price;
      }
    }
    return [...map.values()].sort((a, b) => a.from - b.from || a.name.localeCompare(b.name));
  }, [journeys]);

  const visibleJourneys = useMemo(() => {
    if (airlineFilter.length === 0) return journeys;
    return journeys.filter(j => {
      const code = segmentsFor(j, 'OUTBOUND')[0]?.carrier?.marketingCode;
      return code && airlineFilter.includes(code);
    });
  }, [journeys, airlineFilter]);

  const isReturnSearch = visibleJourneys.some(j => hasReturnLeg(j));

  // Stage one of a return: the distinct outbound flights. Stage two: only the returns that pair
  // with the chosen outbound — the pairing is sparse, so this genuinely narrows the list.
  const outboundGroups = useMemo(
    () => sortLegGroups(groupByLeg(visibleJourneys, 'OUTBOUND'), sortMode),
    [visibleJourneys, sortMode]);

  const chosenOutbound = useMemo(
    () => outboundGroups.find(g => g.key === outboundKey) || null,
    [outboundGroups, outboundKey]);

  const returnGroups = useMemo(
    () => chosenOutbound
      ? sortLegGroups(groupByLeg(chosenOutbound.journeys, 'INBOUND'), sortMode)
      : [],
    [chosenOutbound, sortMode]);

  // The cheapest return total available with the chosen outbound. Each return option is then shown
  // as what it ADDS to that — which is the closest thing to a per-flight price this API allows, and
  // unlike a halved total it is a real number: it is exactly what choosing this return costs.
  const cheapestReturnTotal = useMemo(
    () => (returnGroups.length ? Math.min(...returnGroups.map(g => g.fromPrice)) : 0),
    [returnGroups]);

  const toggleAirline = (code) => setAirlineFilter(prev =>
    prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

  // The trip sector carries a free-text airline ("Qantas", "Virgin", or sometimes just "QF").
  // Match it loosely against what the search actually returned and pre-select that filter, so a
  // sector that already names a carrier doesn't have to be filtered by hand.
  //
  // Pre-selects rather than restricts: if the named airline has nothing on the route, the filter
  // is left off and everything stays visible — silently showing "no results" for a name someone
  // typed weeks ago would be worse than ignoring it.
  const [airlinePreApplied, setAirlinePreApplied] = useState('');
  // Set when the sector names an airline that this search returned nothing for. Saying so matters:
  // silently showing every carrier is indistinguishable from the filter being broken, and provider
  // coverage genuinely varies by date — an airline missing today can be back tomorrow.
  const [airlineUnavailable, setAirlineUnavailable] = useState('');
  useEffect(() => {
    const want = (airlinePre || '').trim();
    if (!want || airlines.length === 0 || airlinePreApplied || airlineUnavailable) return;
    const w = want.toLowerCase();
    const hit = airlines.find(a =>
      a.code.toLowerCase() === w ||
      a.name.toLowerCase().includes(w) ||
      w.includes(a.name.toLowerCase()));
    if (!hit) { setAirlineUnavailable(want); return; }
    setAirlineFilter([hit.code]);
    setAirlinePreApplied(hit.name);
  }, [airlines, airlinePre, airlinePreApplied, airlineUnavailable]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const runSearch = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!origin.trim() || !destination.trim())
      return setError('Choose an origin and destination airport from the suggestions.');
    if (!looksLikeIata(origin) || !looksLikeIata(destination))
      return setError('Pick an airport from the list — flights are searched by airport code.');
    if (!date) return setError('Choose a departure date.');
    const { min, max } = dateBounds();
    if (date < min) return setError('That departure date is in the past.');
    if (date > max) return setError(
      `Airlines only sell about 12 months ahead — choose a date on or before ${max.split('-').reverse().join('/')}.`);
    if (returnDate) {
      if (returnDate < date) return setError('The return date cannot be before the departure date.');
      if (returnDate > max) return setError('That return date is more than 12 months ahead.');
    }

    setBusy(true);
    try {
      const o = origin.trim().toUpperCase();
      const d = destination.trim().toUpperCase();
      // A return is a second leg on the same search, not a second search — the airline prices the
      // pair together and a round trip is usually cheaper than two one-ways.
      const legs = [{ origin: o, destination: d, date, direction: 'OUTBOUND' }];
      if (returnDate) legs.push({ origin: d, destination: o, date: returnDate, direction: 'INBOUND' });

      const data = await liteApi(FLIGHT_ENDPOINTS.rates, {
        legs,
        adults: Number(adults) || 1,
        currency: 'AUD',
        cabinClass,
        // The API returns journeys in no particular order, so a 200-result list is unusable
        // without this — and it makes the "from $X" figures read as a sensible progression.
        sort: { sortBy: 'price', sortOrder: 'asc' },
      }, { retries: 3 });
      const js = (data?.data || []).flatMap(d => d.journeys || []);
      if (js.length === 0) {
        setError('No flights found for those dates. Try a different date or airport.');
        setBusy(false);
        return;
      }
      const cur = offerCurrency((js[0].offers || [])[0] || js[0].cheapestOffer);
      setJourneys(js);
      setCurrency(cur);
      setAirlineFilter([]);
      setAirlinePreApplied('');
      setOutboundKey(null);
      setAirlineUnavailable('');
      setStep('results');
    } catch (err) {
      setError(err.status >= 500
        ? 'The airline search is temporarily unavailable — we retried a few times. Try again in a moment.'
        : (err.message || 'Flight search failed.'));
    } finally {
      setBusy(false);
    }
  };

  const chooseJourney = (j) => { setJourney(j); setOffer(null); setStep('detail'); };

  const chooseOffer = (o) => {
    setOffer(o);
    if (mode === 'quote') return;             // quote stops at the fare choice
    setStep('passengers');
  };

  const saveOption = () => {
    const segs  = segmentsFor(journey, 'OUTBOUND');
    const first = segs[0];
    onSaveOption?.({
      journey, direction: 'OUTBOUND',
      offerId:     offer.offerId,
      journeyKey:  journey.journeyKey,
      origin, destination, date,
      returnDate: returnDate || '',
      isReturn: hasReturnLeg(journey),
      adults: Number(adults) || 1,
      cabinClass,
      price:    offerPrice(offer),
      currency: offerCurrency(offer),
      priceConverted: priceIsConverted(offer),
      includedBaggage: includedBaggage(offer),
      isRefundable: fareTerms(offer).refundable,
      airline:      airlineOf(first),
      flightNumber: flightNumberOf(first),
      international: false,
      ticketName: enteredName,
      // Priced for everyone on the booking, so the approver sees who the fare covers. Without
      // this a two-passenger quote reads as one person's fare and looks simply wrong.
      passengers: paxList.map((p, i) => ({
        ref:         passengerRefFor(i),
        name:        paxNameOf(p) || p.name || '',
        role:        p.role || 'traveller',
        passengerId: p.profile?.id || '',
      })),
      returnSector: returnSectorFrom(journey, {
        cabinClass, includedBaggage: includedBaggage(offer), offerId: offer.offerId, international: false,
      }),
      savedAt: new Date().toISOString(),
    });
  };

  /** Re-price the chosen offer immediately before committing, and surface any change. */
  const verifyAndPrebook = async () => {
    setError('');
    if (!nameConfirmed) return setError('Confirm the passenger name matches their travel document.');
    if (ssrNeeded.length > 0 && !ssrConfirmed)
      return setError('Confirm you will arrange the assistance with the airline — this booking cannot request it for you.');
    // Checked for everyone, naming whoever is incomplete — with two passengers a bare "date of
    // birth is required" leaves the booker guessing which of them it means. Switching to that
    // passenger's tab saves them hunting for it.
    for (let i = 0; i < paxList.length; i++) {
      const p = paxList[i];
      const who = paxNameOf(p) || p.name || `Passenger ${i + 1}`;
      const fail = (msg) => { setActivePax(i); setError(`${who}: ${msg}`); return true; };
      if (!p.firstName.trim() || !p.lastName.trim()) return fail('first and last name are required.');
      if (!p.birthday) return fail('date of birth is required.');
      // The airline rejects a prebook with no gender. Caught here rather than at the fare hold,
      // where it surfaced as "this fare could not be held" and sent people off to pick a
      // different flight over a blank dropdown.
      if (!p.gender) return fail('a gender is required — the airline will not hold the fare without one.');
      // The airline requires all three together — it rejects a number with no expiry — so there is
      // no partial state worth accepting. A domestic traveller can use a licence, not a passport.
      if (!p.documentNumber.trim())
        return fail('a document number is required — the airline will not ticket without one. A driver\'s licence is fine for domestic travel.');
      if (!p.documentExpiry) return fail('the document expiry date is required.');
      if (!p.documentIssueCountry.trim()) return fail('the document issuing country is required.');
      if (!p.nameConfirmed) return fail('confirm the ticket name matches their passport or photo ID.');
    }
    if (!contact.email.trim()) return setError('A contact email is required — the airline sends the itinerary there.');
    if (!contact.phoneNumber.trim()) return setError('A contact phone number is required.');

    setBusy(true);
    try {
      const v = await liteApi(FLIGHT_ENDPOINTS.verify, { offerId: offer.offerId }, { retries: 2 });
      const row = (v?.data || [])[0];
      setVerifyChanges(row?.changes || null);
      const verifiedPrice = parseFloat(row?.journey?.pricing?.display?.total);
      setFinalPrice(Number.isFinite(verifiedPrice) ? verifiedPrice : offerPrice(offer));
      setStep('confirm');
    } catch (err) {
      setError(err.status === 404
        ? 'That fare has expired. Search again to get current prices.'
        : (err.message || 'Could not verify the fare.'));
    } finally {
      setBusy(false);
    }
  };

  const createPrebook = async () => {
    setError('');
    setBusy(true);
    try {
      const data = await liteApi(FLIGHT_ENDPOINTS.prebooks, {
        offerId: offer.offerId,
        usePaymentSdk: true,
        travelPurpose: 'BUSINESS',
        contact: {
          // The first passenger is the booking contact — they are the trip's traveller.
          firstName: paxList[0].firstName.trim(),
          lastName:  paxList[0].lastName.trim(),
          email:     contact.email.trim(),
          phoneCountryCode: contact.phoneCountryCode.replace(/\D/g, ''),
          phoneNumber:      contact.phoneNumber.replace(/\D/g, ''),
        },
        // Order matters and is load-bearing: `passengerRef` is positional, so PAX1 is this array's
        // first entry. Bags and seats are attached against those refs.
        passengers: paxList.map(p => ({
          firstName:  p.firstName.trim(),
          ...(p.middleName.trim() ? { middleName: p.middleName.trim() } : {}),
          lastName:   p.lastName.trim(),
          birthday:   p.birthday,
          passengerType: 0,                    // 0 = adult
          ...(p.gender ? { gender: p.gender } : {}),
          nationality: p.nationality,
          documentType:         normalizeDocumentType(p.documentType),
          documentNumber:       p.documentNumber.trim(),
          documentIssueCountry: p.documentIssueCountry,
          documentExpiry:       p.documentExpiry,
          // Filtered rather than trusted: the API accepts any airlineCode, including nonsense,
          // and passes it straight to the airline where it would credit nobody or the wrong
          // account. Incomplete rows are dropped instead of being sent half-formed.
          ...(validLoyaltyPrograms(p.loyalty).length > 0
            ? { loyaltyPrograms: validLoyaltyPrograms(p.loyalty) } : {}),
        })),
      });
      const row = (data?.data || [])[0] || data;
      setPrebookId(row?.prebookId || '');
      setSecretKey(row?.secretKey || '');
      setTransactionId(row?.transactionId || '');
      const paidPrice = row?.price != null ? parseFloat(row.price) : finalPrice;
      if (row?.price != null) setFinalPrice(paidPrice);
      setPrebookRow(row);
      setBagChoice([]);
      setSeatChoice({});

      // Bags attach to the prebook, so the step has to sit between holding the fare and paying for
      // it. Skip straight past when the carrier offers none — several do not.
      const hasExtras = baggageOptions(row).length > 0 || seatOptions(row).length > 0;
      setStep(hasExtras ? 'bags' : 'payment');
      if (!hasExtras) armPayment(row, paidPrice);
    } catch (err) {
      // Some fares simply cannot be held — measured on this account, one carrier failed every
      // attempt while others on the same route succeeded every time. Pointing at a different fare
      // is far more useful than repeating the airline's wording.
      // A rejection naming a field is a gap in what we sent, not a fare the channel cannot sell.
      // Telling someone to pick a different flight because a dropdown was blank wastes their time
      // and loses the fare they had chosen.
      if (/required|invalid|missing/i.test(err.message || '')) {
        setError(`The airline rejected the booking details (${err.message}). `
          + 'Go back to Passengers, correct the field it names, and try again — the fare itself is fine.');
        setBusy(false);
        setStep('passengers');
        return;
      }
      setError('This fare could not be held'
        + (err.message ? ` (${err.message})` : '')
        + '. That usually means the airline or fare is not bookable through this channel — go back '
        + 'and choose a different flight or fare.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Hand off to payment, writing the recovery snapshot first.
   *
   * Called after bags are settled, never before: attaching a service issues a NEW transactionId and
   * secretKey and a new price, so a snapshot taken at prebook time would point at a superseded
   * payment intent. The published docs call that the most common production bug in this flow.
   */
  const armPayment = (row, price) => {
    const pending = buildBookingPayload({ bookingRef: '', bookingId: '', price });
    let stored = false;
    try {
      sessionStorage.setItem(FLIGHT_STATE_KEY, JSON.stringify({
        prebookId: row?.prebookId || '',
        payload:   pending,
      }));
      stored = sessionStorage.getItem(FLIGHT_STATE_KEY) != null;
    } catch { stored = false; }
    // If it didn't stick, the return URL's prebookId is the only thread left — say so before any
    // money moves rather than after.
    setStorageWarning(!stored);
  };

  /**
   * Attach the chosen bags (or clear them) and move on to payment.
   *
   * Sends the COMPLETE selection every time: each call replaces the previous one outright, so a
   * delta would silently drop bags already chosen on another leg.
   */
  const applyBagsAndPay = async () => {
    setError('');
    // ⚠️ Bags and seats go in ONE call. Each services call replaces the entire selection, so
    // sending them separately would silently drop whichever went first.
    const chosen = [...bagChoice, ...Object.values(seatChoice)];
    if (chosen.length === 0) {
      armPayment(prebookRow, finalPrice);
      setStep('payment');
      return;
    }
    setBusy(true);
    try {
      const data = await liteApi(
        FLIGHT_ENDPOINTS.services(prebookRow.prebookId),
        servicesPayload(chosen, paxList.length),
      );
      const row = (data?.data || [])[0] || data;
      // Every one of these supersedes what prebook returned — using the old pair is the documented
      // way to end up charging the wrong amount.
      if (row?.transactionId) setTransactionId(row.transactionId);
      if (row?.secretKey)     setSecretKey(row.secretKey);
      const newPrice = row?.price != null ? parseFloat(row.price) : finalPrice;
      setFinalPrice(newPrice);
      setPrebookRow(row);
      armPayment(row, newPrice);
      setStep('payment');
    } catch (err) {
      setError('Could not add the selected extras: ' + (err.message || 'unknown error')
        + '. Continue without them, or choose different options.');
    } finally {
      setBusy(false);
    }
  };

  // Bags and seats belong to ONE passenger — a support worker's checked bag is not the traveller's,
  // and two people cannot share a seat. Everything selected is stamped with the positional ref it
  // will be sent under, so the selection and the payload cannot disagree.
  const extrasRef = passengerRefFor(activePax);

  const toggleBag = (svc) => setBagChoice(prev =>
    prev.some(b => b.serviceId === svc.serviceId && b.passengerRef === extrasRef)
      ? prev.filter(b => !(b.serviceId === svc.serviceId && b.passengerRef === extrasRef))
      : [...prev, { ...svc, passengerRef: extrasRef }]);

  const seatKey = (ref, segmentKey) => `${ref}|${segmentKey || ''}`;

  const toggleSeat = (svc) => setSeatChoice(prev => {
    const key = seatKey(extrasRef, svc.segmentKey);
    const next = { ...prev };
    if (next[key]?.serviceId === svc.serviceId) delete next[key];  // tap again to release it
    else next[key] = { ...svc, passengerRef: extrasRef };
    return next;
  });

  const extrasTotal = () => servicesTotal([...bagChoice, ...Object.values(seatChoice)]);

  /**
   * "BNE→SYD · Andrew Sinclair: 1 bag 23kg" — which flight, whose, and what.
   *
   * The leg matters: bags and seats are bought per segment, so a return booking carries a
   * different seat on each flight. Without the route the trip record read "seat 12A, seat 3C"
   * with no way to tell which belonged to which flight.
   */
  const namedExtras = (list) => list.map(svc => {
    const i = Math.max(0, paxList.findIndex((_, n) => passengerRefFor(n) === svc.passengerRef));
    const who = paxNameOf(paxList[i] || {}) || svc.passengerRef || '';
    const what = svc.category === 'seat' ? `seat ${seatNumber(svc)}` : bagLabel(svc);
    const seg  = allSegments().find(s => s.segmentKey === svc.segmentKey);
    const leg  = seg ? `${seg.originCode}→${seg.destinationCode}: ` : '';
    return paxList.length > 1 && who ? `${leg}${who}: ${what}` : `${leg}${what}`;
  }).join(', ');

  /** Every segment on the journey, both directions. */
  const allSegments = () => segmentsFor(journey, 'OUTBOUND').concat(segmentsFor(journey, 'INBOUND'));

  /**
   * The extras bought on one direction's flights.
   *
   * Each leg is its own sector on the trip, so each sector should carry the extras for ITS flight
   * — otherwise everything lands on the outbound and the return leg looks like nothing was bought
   * for it.
   */
  const extrasOnLeg = (list, direction) => {
    const keys = new Set(segmentsFor(journey, direction).map(s => s.segmentKey));
    // No segmentKey at all (some carriers) — keep it on the outbound rather than losing it.
    return list.filter(svc => (svc.segmentKey ? keys.has(svc.segmentKey) : direction === 'OUTBOUND'));
  };

  /**
   * The finished sector payload for a booking. Built from current state at prebook time and
   * stashed, so the post-payment reload does not need the search results to still exist.
   */
  const buildBookingPayload = ({ bookingRef, bookingId, price }) => {
    const segs  = segmentsFor(journey, 'OUTBOUND');
    const first = segs[0];
    return {
      journey, direction: 'OUTBOUND',
      bookingRef, bookingId,
      price:      price ?? finalPrice ?? offerPrice(offer),
      currency:   offerCurrency(offer),
      priceConverted: priceIsConverted(offer),
      cabinClass,
      includedBaggage: includedBaggage(offer),
      purchasedBaggage: namedExtras(extrasOnLeg(bagChoice, 'OUTBOUND')),
      seatsSelected:    namedExtras(extrasOnLeg(Object.values(seatChoice), 'OUTBOUND')),
      // Kept on the sector so that if points never credit, what was actually sent is on record
      // rather than a matter of recollection.
      loyaltySent: paxList.flatMap((p, i) => validLoyaltyPrograms(p.loyalty)
        .map(l => `${paxNameOf(p) || passengerRefFor(i)}: ${l.airlineCode} ${l.programNumber}`))
        .join(', '),
      // Recorded so the trip can show assistance as outstanding until someone logs the
      // airline's confirmation — the booking itself never carried it.
      ssrCodes,
      ssrAcknowledgedAt: ssrNeeded.length > 0 ? new Date().toISOString() : '',
      airline:      airlineOf(first),
      flightNumber: flightNumberOf(first),
      passengerName: allTicketNames.join(', '),
      // Held individually too, so the trip can show who was on the booking and in what order —
      // that order is what PAX1/PAX2 meant when bags and seats were attached.
      passengers: paxList.map((p, i) => ({
        ref:         passengerRefFor(i),
        name:        paxNameOf(p),
        role:        p.role || 'traveller',
        passengerId: p.profile?.id || '',
      })),
      international: false,
      returnSector: returnSectorFrom(journey, {
        cabinClass, includedBaggage: includedBaggage(offer),
        offerId: offer?.offerId, international: false,
        // The return leg's own bags and seats, so they show against the flight they belong to.
        purchasedBaggage: namedExtras(extrasOnLeg(bagChoice, 'INBOUND')),
        seatsSelected:    namedExtras(extrasOnLeg(Object.values(seatChoice), 'INBOUND')),
      }),
      bookedAt: new Date().toISOString(),
    };
  };

  /**
   * Called once the payment wrapper returns with a transaction id.
   *
   * Works from an explicit context because React state does not survive the redirect. Never
   * retried automatically — the money has already moved, and a second call could double-book.
   */
  const confirmBooking = async (tid, ctx = {}) => {
    const usePrebookId = ctx.prebookId || prebookId;
    setError('');
    setBusy(true);
    try {
      const data = await liteApi(FLIGHT_ENDPOINTS.bookings, {
        prebookId: usePrebookId,
        payment: { method: 'TRANSACTION_ID', transactionId: tid || transactionId },
      });
      const row = (data?.data || [])[0] || data;
      const bookingRef = row?.bookingRef || row?.booking?.bookingRef || row?.pnr
        || row?.booking?.order?.pnr || row?.order?.pnr || '';
      const bookingId  = row?.bookingId || row?.booking?.bookingId || '';

      // Prefer the payload stashed before payment; fall back to rebuilding it if the flow was
      // never interrupted and state is still intact.
      const payload = ctx.payload
        ? { ...ctx.payload, bookingRef, bookingId, bookedAt: new Date().toISOString() }
        : buildBookingPayload({ bookingRef, bookingId, price: ctx.price });

      // ⚠️ Do NOT call onBooked here. The page closes the window the moment it fires, so the
      // whole flow used to end by blinking out of existence straight after payment — no
      // confirmation, no reference, no way to tell an approved payment from a declined one.
      // Mirrors the hotel flow: show the outcome, let the booker close it deliberately.
      setBookingOutcome({ ok: true, bookingRef, bookingId, payload, price: ctx.price ?? paidPrice });
      setStep('booked');
    } catch (err) {
      // Deliberately does NOT offer a retry: the payment has already been taken, and calling
      // /flights/bookings again risks a second booking against a second charge.
      setBookingOutcome({
        ok: false,
        message: 'Payment was taken but the booking could not be confirmed: '
          + (err.message || 'unknown error')
          + '. Do NOT pay again — contact STX to have the ticket issued manually.',
      });
      setStep('booked');
    } finally {
      setBusy(false);
    }
  };

  // ── Returning from the payment redirect ────────────────────────────────────
  // The payment wrapper NAVIGATES this window to returnUrl, so the app reloads and every piece of
  // React state is gone — prebookId included. Without the snapshot written before payment there is
  // nothing left to confirm the booking with, and the money would move with no ticket issued.
  // Mirrors what HotelBookingFlow does with hotel_booking_state.
  useEffect(() => {
    const p   = new URLSearchParams(window.location.search);
    const tid = p.get('liteapi_tid');
    const pid = p.get('liteapi_pid');
    if (!tid) return;
    // The wrapper sends us back with PENDING when the payment did not go through — declined card,
    // abandoned 3-D Secure, closed tab. Saying nothing left people staring at the payment step
    // unsure whether they had just been charged.
    if (tid === 'PENDING') {
      window.history.replaceState({}, '',
        window.location.pathname + window.location.search.replace(/[?&]liteapi_[^&]+/g, '').replace(/^&/, '?'));
      setBookingOutcome({
        ok: false,
        declined: true,
        message: 'The payment was not completed, so no fare was held and nothing has been charged. '
          + 'You can go back and try again, or choose a different fare.',
      });
      setStep('booked');
      return;
    }

    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(FLIGHT_STATE_KEY) || 'null'); } catch { /* corrupt */ }

    const ctx = {
      prebookId: (pid && pid !== 'PENDING') ? pid : (saved?.prebookId || prebookId),
      payload:   saved?.payload || null,
    };

    // Strip the payment params so a refresh cannot re-fire the confirmation.
    window.history.replaceState({}, '',
      window.location.pathname + window.location.search.replace(/[?&]liteapi_[^&]+/g, '').replace(/^&/, '?'));

    if (!ctx.prebookId) {
      setError('Payment completed, but the booking reference was lost when this window reloaded. '
        + 'Do NOT pay again — contact STX with the payment reference so the ticket can be issued '
        + 'manually. Transaction: ' + tid);
      return;
    }
    if (!ctx.payload) {
      // The id survived but the flight details did not. Still worth confirming — a ticket issued
      // with a sector to tidy up beats a payment with no ticket at all.
      console.warn('Flight payload lost on reload; confirming from prebookId alone.');
    }
    sessionStorage.removeItem(FLIGHT_STATE_KEY);
    setConfirming(true);
    if (ctx.payload?.price != null) setPaidPrice(ctx.payload.price);
    setStep('payment');
    confirmBooking(tid, ctx).finally(() => setConfirming(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ─────────────────────────────────────────────────────────────────

  const banner = (
    <>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Plane size={18} aria-hidden="true" className="text-blue-600" />
          {mode === 'quote' ? 'Choose a flight option' : 'Book a flight'}
        </h1>
        {NUITEE_IS_SANDBOX && (
          <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">Sandbox</span>
        )}
      </div>
      {mode === 'quote' && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
          <Info size={15} aria-hidden="true" className="text-amber-700 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900">
            This trip is not approved yet, so the flight can be <strong>selected but not booked</strong>.
            Your choice is saved to the trip with its price so the approver can see it, and can be
            booked once the trip is approved.
          </p>
        </div>
      )}
    </>
  );

  const errorBox = error && (
    <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
      <AlertTriangle size={15} aria-hidden="true" className="text-red-600 mt-0.5 shrink-0" />
      <p className="text-sm text-red-700">{error}</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-5">
      {banner}
      <StepBar steps={steps} current={stepIndex} />
      {errorBox}

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      {step === 'search' && (
        <form onSubmit={runSearch} className="space-y-4 bg-white border border-gray-200 rounded-xl p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AirportInput
              id="fl-origin" label="From *" value={origin} onChange={setOrigin}
              placeholder="Brisbane, or BNE" prefillCity={originPre}
            />
            <AirportInput
              id="fl-dest" label="To *" value={destination} onChange={setDestination}
              placeholder="Sydney, or SYD" prefillCity={destinationPre}
            />
            <div>
              <label className={lbl} htmlFor="fl-date">Departure date *</label>
              <input id="fl-date" type="date" className={inp} value={date}
                min={dateBounds().min} max={dateBounds().max}
                onChange={e => setDate(e.target.value)} />
              <p className="text-xs text-gray-600 mt-1">Bookable up to 12 months ahead.</p>
            </div>
            <div>
              <label className={lbl} htmlFor="fl-return">
                Return date <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input id="fl-return" type="date" className={inp} value={returnDate}
                min={date || dateBounds().min} max={dateBounds().max}
                onChange={e => setReturnDate(e.target.value)} />
              <p className="text-xs text-gray-600 mt-1">
                {returnDate ? 'Priced as one return fare.' : 'Leave blank for a one-way.'}
              </p>
            </div>
            <div>
              <label className={lbl} htmlFor="fl-cabin">Cabin</label>
              <select id="fl-cabin" className={inp} value={cabinClass} onChange={e => setCabinClass(e.target.value)}>
                {CABIN_CLASSES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Travelling</label>
              {/* Read-only on purpose. The fare is priced for a number of seats, so the search
                  count and the people ticketed cannot be allowed to drift apart — the list is
                  edited below, where the names are visible. */}
              <p className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg">
                {adults} {adults === 1 ? 'passenger' : 'passengers'}
              </p>
            </div>
          </div>

          {/* Who is on this booking. Taken from the trip, because a support worker travelling with
              the traveller belongs on the same booking — booked separately they can end up on a
              different fare and seated apart. */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-900">Passengers on this booking</p>
              {!pickerOpen && (
                <button type="button" onClick={() => { setPickerOpen(true); setManualOpen(false); }}
                  className="text-xs text-blue-600 hover:text-blue-800">+ Add a passenger</button>
              )}
            </div>
            <ul className="space-y-1.5">
              {paxList.map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-[10px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">
                    {passengerRefFor(i)}
                  </span>
                  <span className="text-gray-900">{p.name || `Passenger ${i + 1}`}</span>
                  {p.role !== 'traveller' && (
                    <span className="text-xs text-gray-700 bg-gray-100 rounded px-1.5 py-0.5">
                      {passengerRoleLabel(p.role)}
                    </span>
                  )}
                  {/* Two different situations, and conflating them misleads: someone who came off
                      the trip with no profile genuinely has to be typed in, whereas someone just
                      added has simply not been identified yet. */}
                  {!p.profile && (
                    <span className="text-xs text-amber-800">
                      {p.name ? 'not in the portal — details entered by hand' : 'name and details still to be entered'}
                    </span>
                  )}
                  {paxList.length > 1 && (
                    <button type="button" onClick={() => removePassenger(i)}
                      aria-label={`Remove ${p.name || `passenger ${i + 1}`} from this booking`}
                      className="ml-auto p-1 text-gray-600 hover:text-red-700">
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>

            {pickerOpen && (
              <div className="mt-3 border border-blue-300 bg-blue-50/50 rounded-lg p-3">
                <label className={lbl} htmlFor="pax-picker">
                  Who else is travelling?
                </label>
                <input
                  id="pax-picker"
                  className={inp}
                  value={pickerText}
                  onChange={e => setPickerText(e.target.value)}
                  placeholder="Start typing a name…"
                  autoFocus
                />
                {pickerMatches.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {pickerMatches.map(p => (
                      <li key={p.id}>
                        <button type="button" onClick={() => addFromProfile(p)}
                          className="w-full text-left px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-blue-400 text-sm">
                          <span className="text-gray-900">{profileName(p)}</span>
                          {(p.identityDocuments || []).length > 0 && (
                            <span className="text-xs text-green-700 ml-2">
                              · ID on file
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-700 mt-2">
                    {clientPassengers.length === 0
                      ? 'No passenger profiles found for this client.'
                      : 'No matching profile.'}
                  </p>
                )}
                <p className="text-xs text-gray-700 mt-2">
                  Choosing a profile brings their travel document and frequent flyer number with
                  them, so nothing has to be retyped.
                </p>

                {/* A one-off support worker or carer often has no profile at all, so this is a
                    first-class path rather than a fallback. Given name and surname are asked for
                    separately instead of split from one box: the split guesses wrong on multi-word
                    surnames, and a wrong name on a ticket is the expensive mistake in this flow. */}
                {!manualOpen ? (
                  <button
                    type="button"
                    onClick={() => {
                      const parts = pickerText.trim().split(/\s+/).filter(Boolean);
                      setManualName({
                        firstName: parts[0] || '',
                        lastName:  parts.length > 1 ? parts.slice(1).join(' ') : '',
                      });
                      setManualOpen(true);
                    }}
                    className="mt-3 w-full px-3 py-2 rounded-lg border border-dashed border-blue-400 bg-white text-sm text-blue-800 hover:bg-blue-50"
                  >
                    + Add someone who has no profile
                  </button>
                ) : (
                  <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-900 mb-2">
                      Someone with no profile
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={lbl} htmlFor="man-first">Given name(s) *</label>
                        <input id="man-first" className={inp} value={manualName.firstName}
                          onChange={e => setManualName(n => ({ ...n, firstName: e.target.value }))}
                          placeholder="As on their photo ID" />
                      </div>
                      <div>
                        <label className={lbl} htmlFor="man-last">Surname *</label>
                        <input id="man-last" className={inp} value={manualName.lastName}
                          onChange={e => setManualName(n => ({ ...n, lastName: e.target.value }))}
                          placeholder="As on their photo ID" />
                      </div>
                    </div>
                    <div>
                      <label className={lbl} htmlFor="man-role">They are travelling as</label>
                      <select id="man-role" className={inp} value={manualRole}
                        onChange={e => setManualRole(e.target.value)}>
                        <option value="support_worker">Support worker</option>
                        <option value="carer">Carer</option>
                        <option value="traveller">Traveller</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-700 mt-2">
                      {mode === 'quote'
                        ? 'Only their name is needed to price the fare. Date of birth and travel document are collected at ticketing, once the trip is approved.'
                        : 'Their date of birth and travel document are entered on their own tab at the next step.'}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <button type="button" onClick={addManualPassenger}
                        disabled={!manualName.firstName.trim() || !manualName.lastName.trim()}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        Add passenger
                      </button>
                      <button type="button" onClick={() => setManualOpen(false)}
                        className="text-xs text-gray-600 hover:text-gray-800">
                        Back
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-3">
                  <button type="button"
                    onClick={() => { setPickerOpen(false); setPickerText(''); setManualOpen(false); }}
                    className="text-xs text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-700 mt-2">
              Everyone here is ticketed on one booking, and the fare is searched for that many
              seats. Removing someone only affects this flight.
            </p>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={busy}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              <Search size={15} aria-hidden="true" />
              {busy ? 'Searching…' : 'Search flights'}
            </button>
          </div>
        </form>
      )}

      {/* ── Results ────────────────────────────────────────────────────────── */}
      {step === 'results' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700">
              {visibleJourneys.length}
              {airlineFilter.length > 0 && ` of ${journeys.length}`}
              {' '}option{visibleJourneys.length !== 1 ? 's' : ''} · {origin.toUpperCase()}{returnDate ? ' ⇄ ' : ' → '}{destination.toUpperCase()} · {date}{returnDate ? ` – ${returnDate}` : ''}
            </p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <span>Sort by</span>
                <select
                  className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={sortMode}
                  onChange={e => setSortMode(e.target.value)}
                  aria-label="Sort flights by"
                >
                  {SORT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <button onClick={() => setStep('search')} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <ChevronLeft size={14} aria-hidden="true" /> Change search
              </button>
            </div>
          </div>

          {airlines.length > 1 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700">Airline</p>
                {airlineFilter.length > 0 && (
                  <button onClick={() => setAirlineFilter([])}
                    className="text-xs text-blue-600 hover:text-blue-800">
                    Clear filter
                  </button>
                )}
              </div>
              {airlinePreApplied && airlineFilter.length > 0 && (
                <p className="mb-2 text-xs text-blue-800">
                  Showing <strong>{airlinePreApplied}</strong> only, because the trip sector names it.
                  Clear the filter to see every airline.
                </p>
              )}
              {airlineUnavailable && (
                <p className="mb-2 text-xs text-amber-800">
                  The trip sector asks for <strong>{airlineUnavailable}</strong>, but this search
                  returned no flights from them — every airline below is shown instead. Availability
                  varies by date, so a different date may bring them back.
                </p>
              )}
              <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by airline">
                {airlines.map(a => {
                  const on = airlineFilter.includes(a.code);
                  return (
                    <button
                      key={a.code}
                      onClick={() => toggleAirline(a.code)}
                      aria-pressed={on}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs transition-colors ${
                        on ? 'bg-blue-600 border-blue-600 text-white'
                           : 'bg-white border-gray-300 text-gray-800 hover:border-blue-400'
                      }`}
                    >
                      {a.logo && <img src={a.logo} alt="" className="w-4 h-4 object-contain" />}
                      <span>{a.name}</span>
                      <span className={on ? 'text-blue-100' : 'text-gray-600'}>
                        ({a.count}) from {money(a.from, currency)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {visibleJourneys.length === 0 ? (
            <p className="text-sm text-gray-700 py-6 text-center">
              No flights match that airline filter.{' '}
              <button onClick={() => setAirlineFilter([])} className="text-blue-600 hover:text-blue-800 underline">
                Clear it
              </button>{' '}to see all {journeys.length} options.
            </p>
          ) : !isReturnSearch ? (
            /* One-way: every flight, in the chosen order. */
            sortJourneys(visibleJourneys, sortMode).map(j => (
              <FlightRow
                key={j.journeyKey}
                segments={segmentsFor(j, 'OUTBOUND')}
                price={offerPrice(j.cheapestOffer || (j.offers || [])[0])}
                currency={currency}
                priceLabel={(j.offers || []).length > 1 ? 'from' : ''}
                note={(j.offers || []).length > 1 ? `${j.offers.length} fares` : ''}
                onSelect={() => chooseJourney(j)}
              />
            ))
          ) : !chosenOutbound ? (
            /* Return, stage one: pick the outbound. */
            <>
              <p className="text-xs font-semibold text-gray-700">
                Step 1 of 2 — choose your outbound flight
              </p>
              {/* Said once, plainly, before any price is read. The airline sells the pair as a
                  single fare — there is no separate outbound price to show, and halving the total
                  would invent a figure nobody quoted. */}
              <p className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                The airline prices a return as <strong>one fare covering both flights</strong>, so
                every price below is the return total — not the cost of the outbound on its own.
              </p>
              {outboundGroups.map(g => (
                <FlightRow
                  key={g.key}
                  segments={g.segments}
                  price={g.fromPrice}
                  currency={currency}
                  priceLabel="Return total from"
                  /* Shown up front because the pairing is sparse: some outbounds leave one return
                     option, others sixteen, and finding that out after choosing is worse. */
                  note={`${g.pairCount} return${g.pairCount === 1 ? '' : 's'} available`}
                  onSelect={() => setOutboundKey(g.key)}
                />
              ))}
            </>
          ) : (
            /* Return, stage two: only the returns that pair with the chosen outbound. */
            <>
              <div className="rounded-lg border border-blue-300 bg-blue-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-blue-800">Outbound chosen</p>
                    <p className="text-sm text-gray-900">
                      {timeOf(chosenOutbound.segments[0]?.departureTime)} {chosenOutbound.segments[0]?.originCode}
                      {' → '}
                      {timeOf(chosenOutbound.segments[chosenOutbound.segments.length - 1]?.arrivalTime)}{' '}
                      {chosenOutbound.segments[chosenOutbound.segments.length - 1]?.destinationCode}
                      <span className="text-gray-700"> · {chosenOutbound.segments.map(x => flightNumberOf(x)).join(' · ')}</span>
                    </p>
                  </div>
                  <button onClick={() => setOutboundKey(null)}
                    className="text-sm text-blue-700 hover:text-blue-900 whitespace-nowrap">
                    Change
                  </button>
                </div>
              </div>
              <p className="text-xs font-semibold text-gray-700">
                Step 2 of 2 — choose your return flight
                <span className="font-normal text-gray-600">
                  {' '}· prices are the return total for both flights, with what each return adds
                </span>
              </p>
              {returnGroups.map(g => (
                <FlightRow
                  key={g.key}
                  segments={g.segments}
                  price={g.fromPrice}
                  currency={currency}
                  priceLabel="Return total"
                  priceNote={g.fromPrice <= cheapestReturnTotal
                    ? 'Cheapest return'
                    : `+${money(g.fromPrice - cheapestReturnTotal, currency)} vs cheapest`}
                  onSelect={() => {
                    // The cheapest journey pairing this outbound with this return; the fare list
                    // on the next step shows the rest.
                    const best = g.journeys
                      .slice()
                      .sort((a, b) => offerPrice(a.cheapestOffer || (a.offers||[])[0])
                                    - offerPrice(b.cheapestOffer || (b.offers||[])[0]))[0];
                    chooseJourney(best);
                  }}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* ── Fare selection for the chosen flight ───────────────────────────── */}
      {step === 'detail' && journey && (
        <div className="space-y-4">
          <button onClick={() => setStep('results')} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <ChevronLeft size={14} aria-hidden="true" />
            {isReturnSearch ? 'Back to return flights' : 'Back to flights'}
          </button>

          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="space-y-2">
              {hasReturnLeg(journey) && (
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-600">Outbound</p>
              )}
              {segmentsFor(journey, 'OUTBOUND').map(s => <SegmentRow key={s.segmentKey} segment={s} />)}
            </div>
            {hasReturnLeg(journey) && (
              <div className="space-y-2 pt-3 border-t border-gray-100">
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-600">Return</p>
                {segmentsFor(journey, 'INBOUND').map(s => <SegmentRow key={s.segmentKey} segment={s} />)}
              </div>
            )}
          </div>

          <h2 className="text-sm font-semibold text-gray-800">Fares on this flight</h2>
          <div className="space-y-2">
            {(journey.offers || []).map(o => {
              const terms = fareTerms(o);
              const bags  = includedBaggage(o);
              const chosen = offer?.offerId === o.offerId;
              return (
                <div key={o.offerId}
                  className={`border rounded-lg p-4 ${chosen ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {o.fare?.family || 'Standard fare'}
                      </p>
                      <p className="text-xs text-gray-700 mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className={terms.refundable ? 'text-green-700' : 'text-gray-700'}>
                          {terms.refundable ? 'Refundable' : 'Non-refundable'}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span>{terms.changeable ? 'Changeable' : 'Non-changeable'}</span>
                        {o.fare?.seatsRemaining > 0 && o.fare.seatsRemaining <= 4 && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="text-amber-700">Only {o.fare.seatsRemaining} left</span>
                          </>
                        )}
                      </p>
                      {bags && (
                        <p className="text-xs text-gray-700 mt-1 flex items-center gap-1">
                          <Luggage size={12} aria-hidden="true" /> {bags}
                        </p>
                      )}
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="text-lg font-semibold text-gray-900 tabular-nums">
                        {money(offerPrice(o), offerCurrency(o))}
                      </p>
                      <button
                        onClick={() => chooseOffer(o)}
                        className="mt-1.5 px-4 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                      >
                        {mode === 'quote' ? 'Choose' : 'Select'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {mode === 'quote' && offer && (
            <div className="sticky bottom-0 bg-white border border-emerald-300 rounded-xl p-4 shadow-lg space-y-3">
              {/* Surfaced here, not just at ticketing. Ticketing can happen weeks after approval and
                  often by someone who has never met the traveller — a wrong legal name found then
                  costs a new fare, whereas found now it costs a profile edit. */}
              <div className={`rounded-lg border px-3 py-2.5 ${
                enteredName ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300'}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle size={14} aria-hidden="true"
                    className={`mt-0.5 shrink-0 ${enteredName ? 'text-amber-700' : 'text-red-600'}`} />
                  <div className="text-sm">
                    {enteredName ? (
                      <>
                        <p className="text-amber-900">
                          Will ticket as{' '}
                          {allTicketNames.map((n, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && ' and '}<strong>{n}</strong>
                            </React.Fragment>
                          ))}
                          {paxList.length > 1 && (
                            <span className="text-xs"> — {paxList.length} passengers on one booking</span>
                          )}
                        </p>
                        <p className="text-xs text-amber-900 mt-0.5">
                          This must match the passport or photo ID exactly. If it does not, fix the
                          passenger profile now — after ticketing a correction usually means buying a
                          new fare. You can still change it at the booking step.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-red-800">No passenger profile is linked to this traveller.</p>
                        <p className="text-xs text-red-800 mt-0.5">
                          The legal name will have to be entered by hand at ticketing. Linking a
                          passenger profile to the trip now avoids that.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-900">
                    <strong>{money(offerPrice(offer), offerCurrency(offer))}</strong> · {offer.fare?.family || 'Standard fare'}
                  </p>
                  <p className="text-xs text-gray-700">Saved to the trip for the approver — not booked.</p>
                </div>
                <button onClick={saveOption}
                  className="px-5 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700">
                  Save this option
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Passengers ─────────────────────────────────────────────────────── */}
      {step === 'passengers' && offer && (
        <div className="space-y-4">
          <button onClick={() => setStep('detail')} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <ChevronLeft size={14} aria-hidden="true" /> Back to fares
          </button>

          {ssrNeeded.length > 0 && (
            <div className="p-4 bg-red-50 border-2 border-red-400 rounded-lg">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={18} aria-hidden="true" className="text-red-700 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-900">
                    This traveller needs assistance that this booking cannot request
                  </p>
                  <ul className="mt-2 space-y-1">
                    {ssrNeeded.map(r => (
                      <li key={r.code} className="text-sm text-red-900">
                        <span className="font-mono text-xs bg-red-100 px-1.5 py-0.5 rounded">{r.code}</span>{' '}
                        {r.label}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-red-900 mt-2">
                    The airline API carries no special-assistance field, so <strong>none of this
                    reaches the airline automatically.</strong> Contact them directly with the booking
                    reference once ticketed, and record the confirmation on the trip.
                  </p>
                  {needsAdvanceNotice(ssrCodes) && (
                    <p className="text-sm font-semibold text-red-900 mt-2">
                      At least one of these needs advance notice — a battery wheelchair, assistance
                      animal or unaccompanied minor can be refused carriage if the airline has not
                      been told before travel.
                    </p>
                  )}
                  <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                    <input type="checkbox" className="mt-0.5" checked={ssrConfirmed}
                      onChange={e => setSsrConfirmed(e.target.checked)} />
                    <span className="text-sm text-red-900">
                      I understand this booking does not request the assistance above, and I will
                      arrange it with the airline directly.
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* The single most expensive mistake in a flight booking. Airlines will not fly a
              passenger whose ticket name differs from their ID, and reissuing costs a fare. */}
          <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={18} aria-hidden="true" className="text-amber-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  Enter the name exactly as it appears on the passport or photo ID
                </p>
                <p className="text-sm text-amber-900 mt-1">
                  Not a preferred or shortened name. Airlines refuse travel when the ticket and the
                  travel document disagree, and corrections after ticketing usually mean buying a new
                  fare. The details below are pre-filled from the portal — <strong>check every field
                  and change anything that does not match the document.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* One tab per person on the booking. Each carries a tick once their details are
              complete, so with a support worker on the ticket it is obvious at a glance whether
              anyone has been left half-filled — the API rejects the whole prebook if so. */}
          {paxList.length > 1 && (
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Passengers on this booking">
              {paxList.map((p, i) => {
                const done = p.firstName.trim() && p.lastName.trim() && p.birthday
                  && p.documentNumber.trim() && p.documentExpiry && p.nameConfirmed;
                return (
                  <button
                    key={i}
                    role="tab"
                    aria-selected={i === activePax}
                    onClick={() => setActivePax(i)}
                    className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${
                      i === activePax
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400'}`}
                  >
                    <span className="font-mono text-[10px] text-gray-600">{passengerRefFor(i)}</span>
                    <span>{paxNameOf(p) || p.name || `Passenger ${i + 1}`}</span>
                    {p.role !== 'traveller' && (
                      <span className="text-xs text-gray-700">· {passengerRoleLabel(p.role)}</span>
                    )}
                    <span aria-hidden="true" className={done ? 'text-green-700' : 'text-gray-400'}>
                      {done ? '✓' : '○'}
                    </span>
                    <span className="sr-only">{done ? 'details complete' : 'details incomplete'}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ⚠️ Says so when nothing was pre-filled. A blank date-of-birth and document section
              looks identical whether the traveller has no profile, the profile could not be read,
              or resolution simply missed — and the booker's reasonable response to all three is to
              retype a passport the portal already holds, or to put their own email on the booking
              as the airline's contact. Both happened on a live booking before this existed. */}
          {pax && !pax.profile && (
            <div className="p-3.5 bg-amber-50 border-2 border-amber-400 rounded-lg">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={16} aria-hidden="true" className="text-amber-800 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    {profileLoadFailed
                      ? 'Passenger profiles could not be loaded — nothing has been pre-filled'
                      : `No passenger profile is linked to ${paxNameOf(pax) || pax.name || 'this passenger'} — nothing has been pre-filled`}
                  </p>
                  <p className="text-xs text-amber-900 mt-1">
                    {profileLoadFailed
                      ? 'Date of birth, travel document and contact details must be entered by hand. Refreshing may fix it; if not, the details below are still safe to type in manually.'
                      : 'Date of birth, travel document, contact details and frequent flyer numbers come from the passenger profile. Link one to fill them in rather than retyping a passport the portal already holds.'}
                  </p>
                  {!profileLoadFailed && clientPassengers.length > 0 && (
                    linkOpen ? (
                      <div className="mt-2.5 space-y-2">
                        <label className="sr-only" htmlFor="link-profile-search">Search passenger profiles</label>
                        <input id="link-profile-search" className={inp} value={linkText} autoFocus
                          onChange={e => setLinkText(e.target.value)}
                          placeholder="Search by name…" />
                        <div className="space-y-1">
                          {linkMatches.map(p => (
                            <button key={p.id} onClick={() => linkProfile(p)}
                              className="w-full text-left px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:border-blue-500">
                              <span className="font-medium text-gray-900">{profileName(p)}</span>
                              {p.dateOfBirth && <span className="text-xs text-gray-700"> · born {p.dateOfBirth}</span>}
                              {(p.identityDocuments || []).length > 0 && (
                                <span className="text-xs text-gray-700"> · {(p.identityDocuments || []).length} document(s) on file</span>
                              )}
                            </button>
                          ))}
                          {linkMatches.length === 0 && (
                            <p className="text-xs text-gray-700">No profile matches that name.</p>
                          )}
                        </div>
                        <button onClick={() => { setLinkOpen(false); setLinkText(''); }}
                          className="text-xs text-gray-700 underline">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => { setLinkOpen(true); setLinkText(paxNameOf(pax) || pax.name || ''); }}
                        className="mt-2 px-3 py-1.5 bg-amber-700 text-white text-xs font-semibold rounded-lg hover:bg-amber-800">
                        Link a passenger profile
                      </button>
                    )
                  )}
                  {!profileLoadFailed && profilesLoaded && clientPassengers.length === 0 && (
                    <p className="text-xs text-amber-900 mt-1.5">
                      This client has no passenger profiles yet, so there is nothing to link.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            {paxList.length > 1 && (
              <p className="text-sm font-semibold text-gray-900 -mb-1">
                {passengerRefFor(activePax)} · {paxNameOf(pax) || pax.name || `Passenger ${activePax + 1}`}
                {pax.role !== 'traveller' && (
                  <span className="font-normal text-gray-700"> — {passengerRoleLabel(pax.role)}</span>
                )}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={lbl} htmlFor="pax-first">Given name(s) *</label>
                <input id="pax-first" className={inp} value={pax.firstName}
                  onChange={e => { setPax(p => ({ ...p, firstName: e.target.value })); setNameConfirmed(false); }}
                  placeholder="As on the document" />
              </div>
              <div>
                <label className={lbl} htmlFor="pax-middle">Middle name(s)</label>
                <input id="pax-middle" className={inp} value={pax.middleName}
                  onChange={e => { setPax(p => ({ ...p, middleName: e.target.value })); setNameConfirmed(false); }}
                  placeholder="If shown on the document" />
              </div>
              <div>
                <label className={lbl} htmlFor="pax-last">Surname *</label>
                <input id="pax-last" className={inp} value={pax.lastName}
                  onChange={e => { setPax(p => ({ ...p, lastName: e.target.value })); setNameConfirmed(false); }}
                  placeholder="As on the document" />
              </div>
            </div>

            {portalName && (
              <div className={`px-3 py-2.5 rounded-lg border text-sm ${
                nameDiffers ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                {nameDiffers ? (
                  <>
                    <strong>Changed from the portal record.</strong> Ticketing as{' '}
                    <strong>{enteredName}</strong> — the portal has <strong>{portalName}</strong>.
                    That is fine if the document says {enteredName}.
                  </>
                ) : (
                  <>Matches the portal record for <strong>{portalName}</strong>. Confirm it also matches the document.</>
                )}
              </div>
            )}

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={nameConfirmed}
                onChange={e => setNameConfirmed(e.target.checked)} />
              <span className="text-sm text-gray-800">
                I have checked <strong>{enteredName || 'this name'}</strong> against the traveller's
                passport or photo ID and it matches exactly.
              </span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
              <div>
                <label className={lbl} htmlFor="pax-dob">Date of birth *</label>
                <input id="pax-dob" type="date" className={inp} value={pax.birthday}
                  onChange={e => setPax(p => ({ ...p, birthday: e.target.value }))} />
              </div>
              <div>
                <label className={lbl} htmlFor="pax-gender">Gender *</label>
                <select id="pax-gender" className={inp} value={pax.gender}
                  onChange={e => setPax(p => ({ ...p, gender: e.target.value }))}>
                  <option value="">Select…</option>
                  <option value="M">M</option>
                  <option value="F">F</option>
                  <option value="X">X</option>
                </select>
                {!pax.gender && (
                  <p className="text-[11px] text-gray-700 mt-1">
                    Required by the airline. Use the value on the travel document.
                  </p>
                )}
              </div>
              <div>
                <label className={lbl} htmlFor="pax-nat">Nationality</label>
                <input id="pax-nat" className={inp} maxLength={2} value={pax.nationality}
                  onChange={e => setPax(p => ({ ...p, nationality: e.target.value.toUpperCase() }))}
                  placeholder="AU" />
              </div>
            </div>

            <p className="text-xs text-gray-700 -mb-1">
              The airline requires a travel document on every fare, including domestic. A driver's
              licence or other photo ID is fine for travel within Australia.
            </p>

            {profileDocs.length > 0 && (
              <div>
                <label className={lbl} htmlFor="doc-pick">Use a document from the profile</label>
                <select
                  id="doc-pick"
                  className={inp}
                  value={docChoice}
                  onChange={e => {
                    setDocChoice(e.target.value);
                    const d = profileDocs.find(x => x.number === e.target.value);
                    if (d) applyDoc(d);
                  }}
                >
                  {profileDocs.map(d => (
                    <option key={d.number} value={d.number}>
                      {d.label}
                      {d.expiry ? ` · expires ${d.expiry}` : ''}
                      {d.valid ? '' : ' — EXPIRED for these dates'}
                    </option>
                  ))}
                  <option value="">Enter a different document…</option>
                </select>
                {docChoice && !profileDocs.find(d => d.number === docChoice)?.valid && (
                  <p role="alert" className="text-xs text-red-700 mt-1">
                    That document expires before this trip. Use another, or update the profile.
                  </p>
                )}
                {docChoice && !profileDocs.find(d => d.number === docChoice)?.country && (
                  <p className="text-xs text-amber-800 mt-1">
                    The profile did not record a recognisable issuing country for this document —
                    check the field below.
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className={lbl} htmlFor="doc-type">Document *</label>
                {/* Only 'passport' and 'id' are accepted — anything else is rejected outright.
                    The API requires a document even on a domestic fare, so 'id' is the default:
                    a driver's licence is what someone actually carries to a domestic flight. */}
                <select id="doc-type" className={inp} value={pax.documentType}
                  onChange={e => setPax(p => ({ ...p, documentType: e.target.value }))}>
                  <option value="id">Driver's licence / photo ID</option>
                  <option value="passport">Passport</option>
                </select>
              </div>
              <div>
                <label className={lbl} htmlFor="doc-num">Number *</label>
                <input id="doc-num" className={inp} value={pax.documentNumber}
                  onChange={e => setPax(p => ({ ...p, documentNumber: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label className={lbl} htmlFor="doc-country">Issuing country *</label>
                <input id="doc-country" className={inp} maxLength={2} value={pax.documentIssueCountry}
                  onChange={e => setPax(p => ({ ...p, documentIssueCountry: e.target.value.toUpperCase() }))}
                  placeholder="AU" />
              </div>
              <div>
                <label className={lbl} htmlFor="doc-exp">Expiry *</label>
                <input id="doc-exp" type="date" className={inp} value={pax.documentExpiry}
                  onChange={e => setPax(p => ({ ...p, documentExpiry: e.target.value }))} />
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-1">
                <label className={lbl}>Frequent flyer</label>
                <button type="button" onClick={addLoyalty}
                  className="text-xs text-blue-600 hover:text-blue-800">+ Add a programme</button>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                {loyaltyResolving
                  ? 'Looking up airline codes from the profile…'
                  : 'Optional. Enter the number exactly as the airline holds it for this traveller.'}
              </p>
              {/* Said plainly because nothing downstream will say it. Neither the API nor the
                  airline checks the number against the passenger, and a mismatch usually fails
                  silently — the points simply never appear. */}
              {!loyaltyResolving && loyalty.length > 0 && (
                <p className="text-xs text-amber-800 mb-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <strong>Not verified.</strong> Nothing checks that a membership number belongs to
                  this traveller — not the portal, not the booking. If it is wrong the points quietly
                  fail to credit, or credit whoever does own that number, and nobody is told. Check it
                  against the traveller's own record before booking.
                </p>
              )}

              {loyalty.length === 0 ? (
                <p className="text-xs text-gray-600">
                  No airline programmes on this passenger's profile.
                </p>
              ) : (
                <div className="space-y-2">
                  {loyalty.map((l, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-24">
                        <input
                          className={inp}
                          maxLength={2}
                          value={l.airlineCode}
                          onChange={e => updateLoyalty(i, 'airlineCode', e.target.value.toUpperCase())}
                          placeholder="QF"
                          aria-label={`Airline code for ${l.sourceName || 'programme ' + (i + 1)}`}
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          className={inp}
                          value={l.programNumber}
                          onChange={e => updateLoyalty(i, 'programNumber', e.target.value)}
                          placeholder="Membership number"
                          aria-label={`Membership number for ${l.sourceName || 'programme ' + (i + 1)}`}
                        />
                        {l.sourceName && !l.airlineCode && (
                          <p className="text-xs text-amber-800 mt-1">
                            Could not match “{l.sourceName}” to an airline — enter the two-letter code,
                            or leave both blank to skip it.
                          </p>
                        )}
                        {l.sourceName && l.airlineCode && (
                          <p className="text-xs text-gray-600 mt-1">From the profile: {l.sourceName}</p>
                        )}
                        {loyaltyNumberWarning(l.programNumber) && (
                          <p className="text-xs text-amber-800 mt-1">
                            {loyaltyNumberWarning(l.programNumber)} It will still be sent as typed.
                          </p>
                        )}
                      </div>
                      <button type="button" onClick={() => removeLoyalty(i)}
                        aria-label="Remove this programme"
                        className="p-2 text-gray-600 hover:text-red-700">
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  {loyalty.length > 0 && validLoyaltyPrograms(loyalty).length < loyalty.length && (
                    <p className="text-xs text-gray-700">
                      Only complete rows are sent — an entry missing its code or number is skipped
                      rather than sent half-formed.
                    </p>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* One contact for the whole booking, not one per passenger — the airline sends the
              itinerary and any disruption notice to a single address. */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-gray-900 mb-3">
              Booking contact
              {paxList.length > 1 && (
                <span className="font-normal text-gray-700"> — for all {paxList.length} passengers</span>
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className={lbl} htmlFor="ct-email">Contact email *</label>
                <input id="ct-email" type="email" className={inp} value={contact.email}
                  onChange={e => setContact(c => ({ ...c, email: e.target.value }))} />
                <p className="text-xs text-gray-600 mt-1">The airline sends the itinerary and any disruption notices here.</p>
              </div>
              <div>
                <label className={lbl} htmlFor="ct-phone">Contact phone *</label>
                <div className="flex gap-1.5">
                  <input aria-label="Country calling code" className={`${inp} w-16`} value={contact.phoneCountryCode}
                    onChange={e => setContact(c => ({ ...c, phoneCountryCode: e.target.value }))} />
                  <input id="ct-phone" className={inp} value={contact.phoneNumber}
                    onChange={e => setContact(c => ({ ...c, phoneNumber: e.target.value }))} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            {expiryMins !== null && (
              <p className={`text-sm flex items-center gap-1.5 ${isExpired ? 'text-red-700' : 'text-gray-700'}`}>
                <Clock size={14} aria-hidden="true" />
                {isExpired ? 'This fare has expired — search again.' : `Fare held for about ${expiryMins} more minute${expiryMins === 1 ? '' : 's'}`}
              </p>
            )}
            <button onClick={verifyAndPrebook} disabled={busy || isExpired}
              className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Checking fare…' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {/* ── Confirm (post-verify) ──────────────────────────────────────────── */}
      {step === 'confirm' && offer && (
        <div className="space-y-4">
          {verifyChanges?.priceChanged && (
            <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                <RefreshCw size={15} aria-hidden="true" /> The price changed when we re-checked it
              </p>
              <p className="text-sm text-amber-900 mt-1">
                Was {money(verifyChanges.pricing?.old?.display?.total, currency)} ·
                {' '}now <strong>{money(verifyChanges.pricing?.new?.display?.total ?? finalPrice, currency)}</strong>.
                {' '}Airlines reprice constantly; continuing books at the new price.
              </p>
              {(verifyChanges.messages || []).map((m, i) => (
                <p key={i} className="text-xs text-amber-900 mt-1">{m}</p>
              ))}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
            <h2 className="text-sm font-semibold text-gray-800">Confirm this booking</h2>
            {segmentsFor(journey, 'OUTBOUND').map(s => <SegmentRow key={s.segmentKey} segment={s} />)}
            {hasReturnLeg(journey) && segmentsFor(journey, 'INBOUND').map(s => <SegmentRow key={s.segmentKey} segment={s} />)}
            <div className="pt-3 border-t border-gray-100 text-sm space-y-1">
              <p className="text-gray-800">
                <strong>Ticketing as:</strong>{' '}
                {paxList.map((p, i) => (
                  <span key={i}>
                    {i > 0 && ' · '}
                    {paxNameOf(p)}
                    {p.role !== 'traveller' && ` (${passengerRoleLabel(p.role).toLowerCase()})`}
                  </span>
                ))}
              </p>
              <p className="text-gray-700">{pax.documentType === 'passport' ? 'Passport' : 'ID'} {pax.documentNumber} · expires {pax.documentExpiry}</p>
              <p className="text-gray-700">{contact.email} · +{contact.phoneCountryCode} {contact.phoneNumber}</p>
            </div>
            <FxNotice offer={offer} currency={currency} />
            <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-700">Total (incl. taxes)</span>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {money(finalPrice ?? paidPrice ?? offerPrice(offer), currency)}
              </span>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep('passengers')} className="text-sm text-blue-600 hover:text-blue-800">
              ← Change details
            </button>
            <button onClick={createPrebook} disabled={busy}
              className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Holding fare…' : 'Continue to payment'}
            </button>
          </div>
        </div>
      )}

      {/* ── Bags ───────────────────────────────────────────────────────────── */}
      {step === 'bags' && prebookRow && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Bags and seats</h2>
              <p className="text-xs text-gray-700 mt-1">
                Optional. Bags are sold per leg, so a bag on one flight does not cover the other.
                Anything the fare already includes is listed on the fare and does not need buying.
                {paxList.length > 1 && ' Each passenger is charged separately — choose for one, then switch.'}
              </p>
            </div>

            {/* Extras are per passenger, so who they are being bought for has to be explicit.
                The count beside each name is what they currently have on this booking. */}
            {paxList.length > 1 && (
              <div className="flex flex-wrap gap-2" role="group" aria-label="Choose extras for">
                {paxList.map((p, i) => {
                  const ref = passengerRefFor(i);
                  const n = bagChoice.filter(b => b.passengerRef === ref).length
                          + Object.values(seatChoice).filter(sv => sv.passengerRef === ref).length;
                  return (
                    <button
                      key={i}
                      onClick={() => setActivePax(i)}
                      aria-pressed={i === activePax}
                      className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${
                        i === activePax
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400'}`}
                    >
                      <span>{paxNameOf(p) || p.name || `Passenger ${i + 1}`}</span>
                      {p.role !== 'traveller' && (
                        <span className="text-xs text-gray-700">· {passengerRoleLabel(p.role)}</span>
                      )}
                      <span className={`text-xs rounded-full px-1.5 ${
                        n > 0 ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-600'}`}>
                        {n}
                      </span>
                      <span className="sr-only">{n === 1 ? '1 extra chosen' : `${n} extras chosen`}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {baggageOptions(prebookRow).length > 0 && baggageBySegment(prebookRow).map(({ segmentKey, services }) => {
              const seg = segmentsFor(journey, 'OUTBOUND').concat(segmentsFor(journey, 'INBOUND'))
                .find(x => x.segmentKey === segmentKey);
              return (
                <div key={segmentKey} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    {seg ? `${seg.originCode} → ${seg.destinationCode}` : 'This flight'}
                    {seg && <span className="font-normal text-gray-600"> · {flightNumberOf(seg)}</span>}
                  </p>
                  <div className="space-y-1.5">
                    {services.map(svc => {
                      const on = bagChoice.some(b => b.serviceId === svc.serviceId && b.passengerRef === extrasRef);
                      return (
                        <label key={svc.serviceId}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer ${
                            on ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 hover:border-gray-300'}`}>
                          <input type="checkbox" checked={on} onChange={() => toggleBag(svc)} />
                          <span className="flex-1 text-sm text-gray-800 flex items-center gap-1.5">
                            <Luggage size={13} aria-hidden="true" className="text-gray-600" />
                            {bagLabel(svc)}
                          </span>
                          <span className="text-sm font-medium text-gray-900 tabular-nums">
                            {money(servicePrice(svc), currency)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {seatOptions(prebookRow).length > 0 && (
              <div className="pt-3 border-t border-gray-100 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">Choose a seat</h3>
                  <p className="text-xs text-gray-700 mt-1">
                    Optional, one per flight. Only the seats the airline offered are shown — an empty
                    space is a seat it did not return, not a free one.
                  </p>
                </div>

                {ssrNeeded.length > 0 && (
                  <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    This traveller needs assistance, so <strong>exit row seats are not permitted</strong> —
                    the occupant has to be able to operate the door unaided. They are blocked below.
                  </p>
                )}

                {seatsBySegment(prebookRow).map(({ segmentKey, services }) => {
                  const seg = segmentsFor(journey, 'OUTBOUND').concat(segmentsFor(journey, 'INBOUND'))
                    .find(x => x.segmentKey === segmentKey);
                  const picked = seatChoice[seatKey(extrasRef, segmentKey)];
                  return (
                    <div key={segmentKey} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-700">
                          {seg ? `${seg.originCode} → ${seg.destinationCode}` : 'This flight'}
                          {seg && <span className="font-normal text-gray-600"> · {flightNumberOf(seg)}</span>}
                        </p>
                        {picked && (
                          <span className="text-xs text-blue-800">
                            Seat <strong>{seatNumber(picked)}</strong> · {money(servicePrice(picked), currency)}
                            {' '}<button onClick={() => toggleSeat(picked)} className="underline">release</button>
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 overflow-x-auto">
                        {seatRows(services).map(({ row, seats }) => (
                          <div key={row} className="flex items-center gap-1">
                            <span className="w-7 shrink-0 text-[10px] text-gray-600 tabular-nums">{row}</span>
                            {seats.map(sv => {
                              const concern = ssrNeeded.length > 0 ? seatAssistanceConcern(sv) : '';
                              const blocked = !seatAvailable(sv) || concern === 'barred';
                              const on = picked?.serviceId === sv.serviceId;
                              const price = servicePrice(sv);
                              return (
                                <button
                                  key={sv.serviceId}
                                  onClick={() => !blocked && toggleSeat(sv)}
                                  disabled={blocked}
                                  title={blocked
                                    ? (concern === 'barred'
                                        ? `${seatNumber(sv)} — exit row, not permitted with special assistance`
                                        : `${seatNumber(sv)} — unavailable`)
                                    : `${seatNumber(sv)} · ${seatDescription(sv) || 'standard'} · ${price > 0 ? money(price, currency) : 'no charge'}${concern === 'check' ? ' · may be an exit row — check before selecting' : ''}`}
                                  className={`w-11 shrink-0 rounded text-[10px] leading-tight py-1 border ${
                                    on ? 'bg-blue-600 border-blue-600 text-white'
                                    : blocked ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed line-through'
                                    : concern === 'check' ? 'bg-amber-50 border-amber-300 text-amber-900 hover:border-amber-500'
                                    : price > 0 ? 'bg-white border-gray-300 text-gray-800 hover:border-blue-400'
                                    : 'bg-green-50 border-green-300 text-green-900 hover:border-green-500'
                                  }`}
                                >
                                  <span className="block font-medium">{sv.metadata?.seat?.seatColumn}</span>
                                  <span className="block">{price > 0 ? money(price, currency).replace('A$', '') : 'free'}</span>
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-gray-700">
                        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300 inline-block" /> no charge</span>
                        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-gray-300 inline-block" /> paid</span>
                        {ssrNeeded.length > 0 && (
                          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300 inline-block" /> may be an exit row — check</span>
                        )}
                        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-200 inline-block" /> unavailable</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-3 border-t border-gray-100 text-sm space-y-1">
              <div className="flex justify-between text-gray-700">
                <span>Fare</span>
                {/* finalPrice is the held fare; extras are not attached until Continue is pressed. */}
                <span className="tabular-nums">{money(finalPrice ?? 0, currency)}</span>
              </div>
              {bagChoice.length > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Bags ({bagChoice.length})</span>
                  <span className="tabular-nums">+ {money(servicesTotal(bagChoice), currency)}</span>
                </div>
              )}
              {Object.keys(seatChoice).length > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Seats ({Object.values(seatChoice).map(sv => seatNumber(sv)).join(', ')})</span>
                  <span className="tabular-nums">+ {money(servicesTotal(Object.values(seatChoice)), currency)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-100">
                <span>To pay</span>
                <span className="tabular-nums">{money((finalPrice ?? 0) + extrasTotal(), currency)}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => { setBagChoice([]); setSeatChoice({}); armPayment(prebookRow, finalPrice); setStep('payment'); }}
              className="text-sm text-blue-600 hover:text-blue-800">
              Skip — no bags or seats
            </button>
            <button onClick={applyBagsAndPay} disabled={busy}
              className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Adding extras…' : 'Continue to payment'}
            </button>
          </div>
        </div>
      )}

      {/* ── Payment ────────────────────────────────────────────────────────── */}
      {step === 'payment' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-800">Payment</h2>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {money(finalPrice ?? paidPrice ?? offerPrice(offer), currency)}
              </span>
            </div>
            {confirming ? (
              <p className="text-sm text-gray-800 flex items-center gap-2">
                <RefreshCw size={14} aria-hidden="true" className="animate-spin" />
                Payment received — confirming your booking with the airline. Do not close this window.
              </p>
            ) : secretKey ? (
              <PaymentWidget
                secretKey={secretKey}
                returnUrl={paymentReturnUrl({
                  liteapi_tid: transactionId || 'PENDING',
                  // Belt and braces: sessionStorage is unavailable in some private modes.
                  liteapi_pid: prebookId || 'PENDING',
                })}
                onError={setError}
              />
            ) : (
              <p className="text-sm text-gray-700">Preparing payment…</p>
            )}
            <FxNotice offer={offer} currency={currency} />
          </div>
          {storageWarning && (
            <div role="alert" className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
              <AlertTriangle size={15} aria-hidden="true" className="text-amber-700 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-900">
                This browser is blocking session storage, so the booking details may not survive the
                payment redirect. If anything goes wrong after paying, do not pay again — contact STX.
              </p>
            </div>
          )}
          <p className="text-xs text-gray-600 flex items-start gap-1.5">
            <Info size={13} aria-hidden="true" className="mt-0.5 shrink-0" />
            Do not close this window until the booking confirms — the fare is held, not ticketed.
          </p>
        </div>
      )}

      {busy && step !== 'search' && (
        <p className="mt-4 text-sm text-gray-700 flex items-center gap-2">
          <RefreshCw size={14} aria-hidden="true" className="animate-spin" /> Working…
        </p>
      )}

      {NUITEE_IS_SANDBOX && step === 'payment' && (
        <p className="mt-3 text-xs text-amber-800">
          Sandbox environment — no real payment is taken and no ticket is issued.
        </p>
      )}

      {/* Confirmation is driven by the return URL; a booked flight closes the popup. */}
      {step === 'booked' && bookingOutcome && (
        <div className="max-w-xl mx-auto space-y-5 text-center">
          <div className={`rounded-full w-20 h-20 flex items-center justify-center mx-auto ${
            bookingOutcome.ok ? 'bg-green-50' : 'bg-red-50'}`}>
            {bookingOutcome.ok
              ? <CheckCircle size={40} aria-hidden="true" className="text-green-600" />
              : <AlertTriangle size={40} aria-hidden="true" className="text-red-600" />}
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {bookingOutcome.ok
                ? 'Payment approved — flight ticketed'
                : bookingOutcome.declined ? 'Payment not completed' : 'Payment taken — ticket NOT confirmed'}
            </h2>
            {bookingOutcome.ok && bookingOutcome.bookingRef && (
              <p className="text-sm text-gray-700 mt-1">
                Airline reference <span className="font-mono font-semibold">{bookingOutcome.bookingRef}</span>
              </p>
            )}
            {bookingOutcome.ok && bookingOutcome.bookingId && (
              <p className="text-xs text-gray-600 mt-0.5">
                Booking id <span className="font-mono">{bookingOutcome.bookingId}</span>
              </p>
            )}
          </div>

          {!bookingOutcome.ok && (
            <div className={`border rounded-lg p-3 text-sm text-left ${
              bookingOutcome.declined
                ? 'bg-amber-50 border-amber-300 text-amber-900'
                : 'bg-red-50 border-red-300 text-red-900'}`}>
              {bookingOutcome.message}
            </div>
          )}

          {bookingOutcome.ok && (() => {
            // ⚠️ Prefer the stored payload over live state. The payment wrapper NAVIGATES this
            // window, so by the time this screen renders the app has reloaded and `journey`,
            // `paxList` and everything else are gone. The payload was snapshotted before payment
            // precisely so the booking survives that — the confirmation has to read it too, or it
            // renders blank on the only path that actually happens.
            const paid      = bookingOutcome.payload || {};
            const shown     = paid.journey || journey;
            const ticketed  = paid.passengerName || allTicketNames.join(', ');
            const legs      = segmentsFor(shown, 'OUTBOUND').concat(segmentsFor(shown, 'INBOUND'))
              .map(sg => `${sg.originCode}→${sg.destinationCode}`).join(' · ');
            return (
            <div className="bg-white border border-gray-200 rounded-xl p-5 text-left space-y-2">
              {ticketed && (
                <div className="flex justify-between text-sm gap-4">
                  <span className="text-gray-600">Ticketed as</span>
                  <span className="font-semibold text-right">{ticketed}</span>
                </div>
              )}
              {legs && (
                <div className="flex justify-between text-sm gap-4">
                  <span className="text-gray-600">Flights</span>
                  <span className="text-right">{legs}</span>
                </div>
              )}
              <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
                <span className="font-bold text-gray-900">Total paid</span>
                <span className="text-xl font-bold text-green-700">
                  {money(bookingOutcome.price ?? paid.price ?? paidPrice, paid.currency || currency)}
                </span>
              </div>
            </div>
            );
          })()}

          <div className="space-y-3">
            {bookingOutcome.ok ? (
              <>
                <button
                  onClick={() => onBooked?.(bookingOutcome.payload)}
                  className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
                  <Check size={16} aria-hidden="true" /> Add to trip &amp; close this window
                </button>
                <p className="text-xs text-gray-700">
                  This fills in the flight sector on the trip and closes this window.
                  {needsTripSave
                    ? ' The trip form will still be open — review it and press Save to keep the booking.'
                    : ' The trip is updated straight away; nothing further to save.'}
                </p>
              </>
            ) : (
              <>
                {bookingOutcome.declined && (
                  <button
                    onClick={() => { setBookingOutcome(null); setStep('search'); }}
                    className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">
                    Search again
                  </button>
                )}
                <button
                  onClick={() => window.close()}
                  className="w-full py-2.5 border border-gray-300 text-gray-800 font-semibold rounded-lg hover:bg-gray-50">
                  Close this window
                </button>
                <p className="text-xs text-gray-700">
                  Nothing has been written to the trip.
                  {!bookingOutcome.declined && ' Contact STX before attempting any further payment.'}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
